    import {
      fetchGenerations, fetchArPhotos, fetchMapPhotos, fetchSpherePhotos, uploadSpherePhoto,
      fetchAdminSession, logoutAdmin, createEventsStream,
    } from '../../services/apiClient.js';
    import { withPngDpi } from '../../utils/pngDpi.js';
    import { sphereCaptionSlug } from '../../utils/photoArchive.js';
    import { generateSVG } from '../../gen_svg.js';
    import { resolveStaticSvg } from '../../utils/knockoutRecord.js';
    import { initHeaderMenu } from '../ui/headerMenu.js';
    import { escapeHtml } from '../../utils/stringUtils.js';
    import { getCachedIsAdmin, setCachedIsAdmin } from '../../services/adminUiCache.js';
    import { createAlphabetSphere, computeWeightsFromGenerations } from './alphabetSphere.js';
    import { buildSillageLoop } from './sillageLoop.js';
    import { drawPanelOverlay } from './spherePhotoOverlay.js';

    // ── Coquille de page (identique au motif ar.html) ─────────────────────
    // Sert aussi à générer le SVG des punchlines affichées dans le panneau
    // de détail au clic sur un dépôt (cf. plus bas) — pas seulement au logo.
    let cachedFontBuffer = null;
    async function loadFontBuffer() {
      if (cachedFontBuffer) return cachedFontBuffer;
      const res = await fetch('/assets/fonts/nocode.otf');
      if (!res.ok) throw new Error('Font not found');
      cachedFontBuffer = await res.arrayBuffer();
      return cachedFontBuffer;
    }

    initHeaderMenu({
      headerEl: document.querySelector('header'),
      burgerBtn: document.getElementById('burger-btn'),
      navLinks: document.querySelectorAll('.header-nav .nav-link'),
    });

    (async () => {
      const dashboardLink = document.getElementById('dashboard-link');
      const adminLogoutBtn = document.getElementById('adminLogoutBtn');
      // Affichage optimiste depuis le dernier statut admin connu, le temps
      // que la vraie vérification serveur réponde — évite que Dashboard/
      // Déconnecter clignotent à chaque chargement de page pour l'admin.
      const cachedIsAdmin = getCachedIsAdmin();
      if (dashboardLink) dashboardLink.hidden = !cachedIsAdmin;
      if (adminLogoutBtn) adminLogoutBtn.hidden = !cachedIsAdmin;
      // Boutons d'export vidéo (plein écran, cf. sillage.html).
      document.body.classList.toggle('sillage-admin', cachedIsAdmin);
      try {
        const data = await fetchAdminSession();
        const isAdmin = Boolean(data?.isAdmin);
        if (dashboardLink) dashboardLink.hidden = !isAdmin;
        if (adminLogoutBtn) adminLogoutBtn.hidden = !isAdmin;
        document.body.classList.toggle('sillage-admin', isAdmin);
        setCachedIsAdmin(isAdmin);
      } catch {
        if (dashboardLink) dashboardLink.hidden = true;
        if (adminLogoutBtn) adminLogoutBtn.hidden = true;
        document.body.classList.remove('sillage-admin');
        setCachedIsAdmin(false);
      }
    })();

    const adminLogoutBtn = document.getElementById('adminLogoutBtn');
    if (adminLogoutBtn) {
      adminLogoutBtn.addEventListener('click', () => {
        adminLogoutBtn.disabled = true;
        logoutAdmin().catch(() => {}).finally(() => { window.location.href = '/'; });
      });
    }

    // ── Plein écran (même mécanique que ar.html, classe/ids renommés) ────
    let sillageFullscreen = false;
    let hideUITimer = null;
    const fsBtn = document.getElementById('sillage-fullscreen-btn');
    const fsIconExpand = fsBtn.querySelector('.icon-expand');
    const fsIconCompress = fsBtn.querySelector('.icon-compress');
    const fsHoverTrigger = document.getElementById('sillage-hover-trigger');
    const sillageHeaderEl = document.querySelector('header');
    const sillageFooterEl = document.querySelector('footer');
    const proximityBtn = document.getElementById('sillage-proximity-btn');
    const globeBtn = document.getElementById('sillage-globe-btn');
    const infoBtn = document.getElementById('sillage-info-btn');
    const exportLandscapeBtn = document.getElementById('sillage-export-landscape-btn');
    const exportPortraitBtn = document.getElementById('sillage-export-portrait-btn');
    const photoBtn = document.getElementById('sillage-photo-btn');
    const contributorsBtn = document.getElementById('sillage-contributors-btn');
    // Boutons du bas, masqués en plein écran jusqu'au survol.
    const fullscreenButtons = [
      fsBtn, proximityBtn, globeBtn, infoBtn, contributorsBtn, photoBtn, exportLandscapeBtn, exportPortraitBtn,
    ];

    // Viseur de la photo 30x30 cm (cf. takeSpherePhoto plus bas) : déclaré
    // ici, la sortie du plein écran le referme.
    const viewfinderEl = document.getElementById('sillage-viewfinder');
    const viewfinderFrame = document.getElementById('sillage-viewfinder-frame');

    function openViewfinder() {
      viewfinderEl.hidden = false;
      photoBtn.classList.add('is-active');
      photoBtn.setAttribute('aria-pressed', 'true');
    }

    function closeViewfinder() {
      viewfinderEl.hidden = true;
      photoBtn.classList.remove('is-active');
      photoBtn.setAttribute('aria-pressed', 'false');
      fitPanelToViewfinder();
    }

    // markInteraction est déclarée dans le bloc try de la scène, plus bas :
    // invisible d'ici (l'appel direct levait une ReferenceError à chaque
    // entrée en plein écran). Branchée une fois la scène créée.
    let onEnterFullscreen = () => {};

    function enterFullscreen() {
      sillageFullscreen = true;
      document.body.classList.add('sillage-fullscreen');
      fsIconExpand.hidden = true;
      fsIconCompress.hidden = false;
      onEnterFullscreen();
    }

    function exitFullscreen() {
      sillageFullscreen = false;
      clearTimeout(hideUITimer);
      document.body.classList.remove('sillage-fullscreen');
      sillageHeaderEl.classList.remove('header--visible');
      sillageFooterEl.classList.remove('footer--visible');
      for (const btn of fullscreenButtons) btn.classList.remove('btn-visible');
      fsIconExpand.hidden = false;
      fsIconCompress.hidden = true;
      // Le viseur photo n'existe qu'en plein écran.
      closeViewfinder();
    }

    function showFullscreenUI() {
      clearTimeout(hideUITimer);
      sillageHeaderEl.classList.add('header--visible');
      sillageFooterEl.classList.add('footer--visible');
      for (const btn of fullscreenButtons) btn.classList.add('btn-visible');
    }

    function scheduleHideFullscreenUI() {
      clearTimeout(hideUITimer);
      hideUITimer = setTimeout(() => {
        sillageHeaderEl.classList.remove('header--visible');
        sillageFooterEl.classList.remove('footer--visible');
        for (const btn of fullscreenButtons) btn.classList.remove('btn-visible');
      }, 800);
    }

    fsHoverTrigger.addEventListener('mouseenter', showFullscreenUI);
    sillageHeaderEl.addEventListener('mouseenter', showFullscreenUI);
    sillageHeaderEl.addEventListener('mouseleave', scheduleHideFullscreenUI);
    sillageFooterEl.addEventListener('mouseenter', showFullscreenUI);
    sillageFooterEl.addEventListener('mouseleave', scheduleHideFullscreenUI);
    fsBtn.addEventListener('mouseenter', showFullscreenUI);
    fsBtn.addEventListener('mouseleave', scheduleHideFullscreenUI);
    for (const btn of [contributorsBtn, photoBtn, exportLandscapeBtn, exportPortraitBtn]) {
      btn.addEventListener('mouseenter', showFullscreenUI);
      btn.addEventListener('mouseleave', scheduleHideFullscreenUI);
    }

    fsBtn.addEventListener('click', () => {
      if (sillageFullscreen) exitFullscreen();
      else enterFullscreen();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sillageFullscreen) exitFullscreen();
      else if ((e.key === 'f' || e.key === 'F') && !sillageFullscreen) enterFullscreen();
      else if (e.key === 'Escape' && legendOpen) closeLegend();
    });

    // ── Légende (popup au clic sur le bouton "i") ────────────────────────
    const legend = document.getElementById('sillage-legend');
    let legendOpen = false;

    function closeLegend() {
      legendOpen = false;
      legend.classList.remove('is-open');
      legend.setAttribute('aria-hidden', 'true');
      infoBtn.classList.remove('is-active');
      infoBtn.setAttribute('aria-expanded', 'false');
      infoBtn.setAttribute('aria-label', 'Afficher la légende');
    }

    function openLegend() {
      legendOpen = true;
      legend.classList.add('is-open');
      legend.setAttribute('aria-hidden', 'false');
      infoBtn.classList.add('is-active');
      infoBtn.setAttribute('aria-expanded', 'true');
      infoBtn.setAttribute('aria-label', 'Masquer la légende');
    }

    infoBtn.addEventListener('click', () => {
      if (legendOpen) closeLegend();
      else openLegend();
    });

    // Clic en dehors du popup (et pas sur le bouton lui-même, déjà géré par
    // le toggle ci-dessus) -- même geste que fermer un menu classique.
    document.addEventListener('pointerdown', (e) => {
      if (!legendOpen) return;
      if (legend.contains(e.target) || infoBtn.contains(e.target)) return;
      closeLegend();
    });

    // ── Panneau de détail ──────────────────────────────────────────────
    const panelBackdrop = document.getElementById('sillage-panel-backdrop');
    const panelEl = document.getElementById('sillage-panel');
    const panelContent = document.getElementById('sillage-panel-content');
    const panelClose = document.getElementById('sillage-panel-close');

    let panelGroup = [];
    let panelIndex = 0;
    // Fin du dernier pincement sur le fond du panneau (zoom de la sphère,
    // plus bas) : le tap qui peut suivre ne referme pas le panneau.
    let panelPinchEndedAt = 0;

    // Viseur photo ouvert : la fenêtre phrase/photo est centrée dans le
    // carré, la photo AR (ou la largeur de la punchline) ramenée à
    // PANEL_FRAME_RATIO de son côté -- 15 cm sur 30, choix de Rudy le
    // 22 octobre 2026 -- quelle que soit la taille de l'écran. Mise à
    // l'échelle par transform : marges et police suivent, et
    // drawPanelOverlay reçoit le facteur (panelFrameScale).
    const PANEL_FRAME_RATIO = 0.5;
    let panelFrameScale = 1;

    function fitPanelToViewfinder() {
      const content = panelEl.querySelector('.popup-svg-wrap svg, .popup-photo-wrap img');
      panelEl.style.transform = '';
      if (viewfinderEl.hidden || panelBackdrop.hidden || !content) {
        panelEl.style.position = '';
        panelEl.style.left = '';
        panelEl.style.top = '';
        panelEl.style.maxHeight = '';
        panelFrameScale = 1;
        return;
      }
      const frame = viewfinderFrame.getBoundingClientRect();
      panelFrameScale = frame.width * PANEL_FRAME_RATIO / content.getBoundingClientRect().width;
      panelEl.style.position = 'fixed';
      panelEl.style.left = `${frame.left + frame.width / 2}px`;
      panelEl.style.top = `${frame.top + frame.height / 2}px`;
      panelEl.style.maxHeight = 'none';
      panelEl.style.transform = `translate(-50%, -50%) scale(${panelFrameScale})`;
    }

    function closePanel() {
      panelBackdrop.hidden = true;
      panelContent.innerHTML = '';
      panelGroup = [];
      panelIndex = 0;
    }

    async function renderPanelItem() {
      const deposit = panelGroup[panelIndex];
      panelContent.innerHTML = '';
      if (deposit.kind === 'punchline') {
        const wrap = document.createElement('div');
        wrap.className = 'popup-svg-wrap';
        try {
          const buf = await loadFontBuffer();
          wrap.innerHTML = await resolveStaticSvg({
            script: deposit.script, text: deposit.text, knockoutSvg: deposit.knockoutSvg,
            generateSVG, fontBuffer: buf, color: '#ffffff',
          });
        } catch {
          wrap.innerHTML = `<strong>${escapeHtml(deposit.text || '')}</strong>`;
        }
        panelContent.appendChild(wrap);
      } else {
        const wrap = document.createElement('div');
        wrap.className = 'popup-photo-wrap';
        wrap.innerHTML = `<img src="${deposit.imagePath}" alt="photo">`;
        // Largeur de l'image inconnue avant son chargement (cadrage viseur).
        wrap.querySelector('img').addEventListener('load', fitPanelToViewfinder);
        panelContent.appendChild(wrap);
      }
      if (deposit.pseudo) {
        const pseudoEl = document.createElement('div');
        pseudoEl.className = 'popup-pseudo';
        pseudoEl.textContent = deposit.pseudo;
        panelContent.appendChild(pseudoEl);
      }
      if (panelGroup.length > 1) {
        const navRow = document.createElement('div');
        navRow.className = 'popup-nav-row';
        navRow.innerHTML = `
          <button class="popup-btn-prev" aria-label="Précédent">‹</button>
          <span class="popup-nav-count">${panelIndex + 1} / ${panelGroup.length}</span>
          <button class="popup-btn-next" aria-label="Suivant">›</button>
        `;
        navRow.querySelector('.popup-btn-prev').addEventListener('click', (e) => {
          e.stopPropagation();
          panelIndex = (panelIndex - 1 + panelGroup.length) % panelGroup.length;
          renderPanelItem();
        });
        navRow.querySelector('.popup-btn-next').addEventListener('click', (e) => {
          e.stopPropagation();
          panelIndex = (panelIndex + 1) % panelGroup.length;
          renderPanelItem();
        });
        panelContent.appendChild(navRow);
      }
      fitPanelToViewfinder();
    }

    function openPanel(group, startIndex) {
      panelGroup = group;
      panelIndex = startIndex || 0;
      panelBackdrop.hidden = false;
      renderPanelItem();
    }

    panelClose.addEventListener('click', closePanel);
    panelBackdrop.addEventListener('click', (e) => {
      if (e.target === panelBackdrop && performance.now() - panelPinchEndedAt > 400) closePanel();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !panelBackdrop.hidden) closePanel();
    });

    // ── Données ────────────────────────────────────────────────────────
    function buildDeposits(generations, arPhotos, mapPhotos) {
      const deposits = [];
      for (const row of generations) {
        if (row.lat == null || row.lng == null) continue;
        deposits.push({
          id: 'gen-' + row.id,
          kind: 'punchline',
          lat: row.lat,
          lng: row.lng,
          pseudo: (row.pseudo || '').trim(),
          createdAt: row.createdAt,
          text: row.inputText,
          script: row.script || 'Latn',
          knockoutSvg: row.knockoutSvg || null,
        });
      }
      for (const row of arPhotos) {
        if (row.lat == null || row.lng == null) continue;
        deposits.push({
          id: 'ar-' + row.id,
          kind: 'ar',
          lat: row.lat,
          lng: row.lng,
          pseudo: (row.pseudo || '').trim(),
          createdAt: row.createdAt,
          imagePath: `/api/ar-photos/${row.id}/image?size=web`,
        });
      }
      for (const row of mapPhotos) {
        if (row.lat == null || row.lng == null) continue;
        deposits.push({
          id: 'map-' + row.id,
          kind: 'map',
          lat: row.lat,
          lng: row.lng,
          pseudo: (row.pseudo || '').trim(),
          createdAt: row.createdAt,
          imagePath: `/api/map-photos/${row.id}/image?size=web`,
        });
      }
      return deposits;
    }

    // Regroupe les dépôts (punchlines, photos AR, photos carte confondues) qui
    // sont à moins de PROXIMITY_THRESHOLD_KM les uns des autres à vol d'oiseau
    // (union-find sur la vraie distance géographique, pas un simple arrondi de
    // coordonnées) : ils partagent alors un seul point visuel (positionné au
    // centre du groupe) et un carrousel prev/next au clic.
    const PROXIMITY_THRESHOLD_KM = 1;

    function haversineKm(lat1, lng1, lat2, lng2) {
      const R = 6371;
      const dLat = THREE.MathUtils.degToRad(lat2 - lat1);
      const dLng = THREE.MathUtils.degToRad(lng2 - lng1);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(THREE.MathUtils.degToRad(lat1)) * Math.cos(THREE.MathUtils.degToRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    }

    function buildProximityGroups(deposits) {
      const parent = deposits.map((_, i) => i);
      function find(i) {
        while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
        return i;
      }
      function union(i, j) {
        const ri = find(i);
        const rj = find(j);
        if (ri !== rj) parent[ri] = rj;
      }
      for (let i = 0; i < deposits.length; i++) {
        for (let j = i + 1; j < deposits.length; j++) {
          if (haversineKm(deposits[i].lat, deposits[i].lng, deposits[j].lat, deposits[j].lng) < PROXIMITY_THRESHOLD_KM) {
            union(i, j);
          }
        }
      }
      const byRoot = new Map();
      deposits.forEach((deposit, i) => {
        const root = find(i);
        if (!byRoot.has(root)) byRoot.set(root, []);
        byRoot.get(root).push(deposit);
      });
      const groups = [];
      for (const items of byRoot.values()) {
        items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        groups.push({
          items,
          lat: items.reduce((sum, d) => sum + d.lat, 0) / items.length,
          lng: items.reduce((sum, d) => sum + d.lng, 0) / items.length,
        });
      }
      return groups;
    }

    function buildDeambulationGroups(deposits) {
      const byPseudo = new Map();
      for (const deposit of deposits) {
        if (!deposit.pseudo) continue;
        if (!byPseudo.has(deposit.pseudo)) byPseudo.set(deposit.pseudo, []);
        byPseudo.get(deposit.pseudo).push(deposit);
      }
      const groups = [];
      for (const [pseudo, items] of byPseudo) {
        if (items.length < 2) continue;
        items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        groups.push({ pseudo, items });
      }
      return groups;
    }

    // Couleur par contributeur, attribuée dans l'ordre de PREMIÈRE
    // CONTRIBUTION géolocalisée (cf. pré-passage de buildScene) -- un nouveau
    // contributeur arrive donc toujours en dernier et ne décale personne.
    // Avant, l'ordre suivait celui du dashboard (réordonnable à la main) puis
    // des photos (plus récentes en premier) : réordonner ou poster une photo
    // changeait la couleur d'autres contributeurs. Avant aussi :
    // angle doré (137.508°) à saturation/luminosité fixes -- seule la teinte
    // variait, et au-delà de ~20 contributeurs deux teintes voisines
    // devenaient indiscernables (cas réel, 28 septembre 2026 : @anne.lise43
    // à 190° et @pochprado à 178°, deux cyans quasi identiques).
    // Maintenant : chaque nouveau contributeur reçoit, parmi une grille de
    // candidats (teinte tous les 5° × 3 luminosités), celui qui est le PLUS
    // ÉLOIGNÉ de toutes les couleurs déjà attribuées, distance mesurée en
    // OKLab (espace perceptuel -- un écart de teinte ne se voit pas pareil
    // selon la zone du cercle, jaunes/verts notamment). Écart minimal entre
    // deux contributeurs ~3,5x plus grand qu'avec l'angle doré pour 24
    // pseudos. Luminosités bornées (0.48-0.76) pour rester lisibles sur le
    // fond noir.
    const PSEUDO_HUE_STEP_DEG = 5;
    const PSEUDO_LIGHTNESS_LEVELS = [0.48, 0.6, 0.76];
    const PSEUDO_SATURATION = 0.85;

    function hslToOklab(h, s, l) {
      const k = (n) => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
      const lin = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
      const [r, g, b] = [f(0), f(8), f(4)].map(lin);
      const lc = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
      const mc = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
      const sc = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
      return [
        0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc,
        1.9779984951 * lc - 2.428592205 * mc + 0.4505937099 * sc,
        0.0259040371 * lc + 0.7827717662 * mc - 0.808675766 * sc,
      ];
    }

    const pseudoColorCandidates = [];
    for (let h = 0; h < 360; h += PSEUDO_HUE_STEP_DEG) {
      for (const l of PSEUDO_LIGHTNESS_LEVELS) {
        pseudoColorCandidates.push({ h, l, lab: hslToOklab(h, PSEUDO_SATURATION, l) });
      }
    }

    // Premier contributeur : rouge vif (teinte 0°, luminosité médiane) --
    // sans ce point de départ, le choix glouton prendrait simplement le
    // premier candidat de la grille (rouge sombre).
    const FIRST_PSEUDO_COLOR = pseudoColorCandidates.find((c) => c.h === 0 && c.l === 0.6);
    const takenPseudoLabs = [];

    const pseudoColorCache = new Map();
    function colorForPseudo(pseudo) {
      if (!pseudo) return null;
      if (pseudoColorCache.has(pseudo)) return pseudoColorCache.get(pseudo);
      let pick = takenPseudoLabs.length === 0 ? FIRST_PSEUDO_COLOR : null;
      if (!pick) {
        let bestDist = -1;
        for (const candidate of pseudoColorCandidates) {
          let nearest = Infinity;
          for (const lab of takenPseudoLabs) {
            const d = Math.hypot(candidate.lab[0] - lab[0], candidate.lab[1] - lab[1], candidate.lab[2] - lab[2]);
            if (d < nearest) nearest = d;
          }
          if (nearest > bestDist) {
            bestDist = nearest;
            pick = candidate;
          }
        }
      }
      takenPseudoLabs.push(pick.lab);
      const color = new THREE.Color().setHSL(pick.h / 360, PSEUDO_SATURATION, pick.l);
      pseudoColorCache.set(pseudo, color);
      return color;
    }

    // Pseudos distincts présents dans un groupe de dépôts co-localisés (les
    // groupes issus du regroupement par proximité peuvent mélanger plusieurs
    // contributeurs), dans l'ordre de première apparition.
    function distinctPseudos(items) {
      const seen = new Set();
      const list = [];
      for (const item of items) {
        if (item.pseudo && !seen.has(item.pseudo)) {
          seen.add(item.pseudo);
          list.push(item.pseudo);
        }
      }
      return list;
    }

    // ── Scène 3D ───────────────────────────────────────────────────────
    const SPHERE_RADIUS = 5;
    const POINT_RADIUS = 0.06;
    const CAMERA_FOV = 55;

    function latLngToVector3(lat, lng, r) {
      const phi = (90 - lat) * Math.PI / 180;
      const theta = (lng + 180) * Math.PI / 180;
      return new THREE.Vector3(
        -r * Math.sin(phi) * Math.cos(theta),
         r * Math.cos(phi),
         r * Math.sin(phi) * Math.sin(theta),
      );
    }

    // Interpolation sphérique (grand cercle) entre deux points déjà posés sur
    // la sphère : donne un arc de même courbure que le globe, plutôt qu'une
    // corde droite qui couperait à travers l'espace 3D.
    function slerpOnSphere(a, b, t, radius) {
      const angle = a.angleTo(b);
      if (angle < 1e-6) return a.clone();
      const sinAngle = Math.sin(angle);
      const w1 = Math.sin((1 - t) * angle) / sinAngle;
      const w2 = Math.sin(t * angle) / sinAngle;
      return new THREE.Vector3(
        a.x * w1 + b.x * w2,
        a.y * w1 + b.y * w2,
        a.z * w1 + b.z * w2,
      ).normalize().multiplyScalar(radius);
    }

    const ARC_SEGMENTS = 16; // subdivisions par paire de points, pour lisser la courbe
    function curveDeambulationPath(points, colors) {
      const curvedPoints = [];
      const curvedColors = [];
      for (let i = 0; i < points.length - 1; i++) {
        const startSegment = i === 0 ? 0 : 1;
        for (let s = startSegment; s <= ARC_SEGMENTS; s++) {
          const t = s / ARC_SEGMENTS;
          curvedPoints.push(slerpOnSphere(points[i], points[i + 1], t, SPHERE_RADIUS));
          curvedColors.push(colors[i].clone().lerp(colors[i + 1], t));
        }
      }
      return { points: curvedPoints, colors: curvedColors };
    }

    // ── Filet de sécurité ──────────────────────────────────────────────────
    // Toute la construction du globe ci-dessous est synchrone (scène, caméra,
    // WebGLRenderer, géométrie...) : si le CDN/la lib three.js échoue à se
    // charger, ou si la création du contexte WebGL échoue une fois, une
    // erreur ici arrêtait tout le script sans aucun message — le canvas
    // restait vide et il fallait recharger la page pour retenter. On affiche
    // maintenant un message explicite avec un bouton pour relancer.
    function showSillageError(error) {
      console.error('Sillage init error:', error);
      const panel = document.getElementById('sillage-error');
      if (!panel) return;
      panel.hidden = false;
    }

    try {

    const canvas = document.getElementById('sillage-canvas');

    // La perte de contexte WebGL (limite de contextes actifs atteinte —
    // fréquent en enchaînant plusieurs pages avec WebGL comme AR/Sillage
    // sans laisser le temps au navigateur de libérer les précédentes) ne
    // lève jamais d'exception : les appels GL deviennent silencieusement
    // des no-op, le canvas reste noir/vide sans jamais passer par le
    // try/catch ci-dessous ni par celui de la boucle de rendu. On l'écoute
    // explicitement pour afficher le même message d'erreur dans ce cas.
    canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      showSillageError(new Error('Contexte WebGL perdu'));
    }, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(CAMERA_FOV, window.innerWidth / window.innerHeight, 0.005, 100);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 1);

    // ── Centrage vertical dans la zone utile (hors header/footer/barre) ───
    // La zone "sûre" est le rectangle disponible entre le bas du header (ou
    // de la barre d'aide si elle est visible) et le haut du footer, avec une
    // marge de chaque côté. On centre le globe dedans (fenêtre de vue
    // asymétrique via setViewOffset, sans déformer la perspective) et on le
    // dimensionne pour qu'il y tienne pile — cf. computeDefaultView().
    const SAFE_ZONE_GAP_PX = 20;
    // Marge horizontale minimale (cadrage par défaut) : sans elle, sur un
    // écran étroit et haut (mobile portrait), le rayon calculé pour tenir
    // dans la hauteur disponible peut largement déborder en largeur — le
    // globe touche alors les bords gauche/droite.
    const SAFE_ZONE_SIDE_MARGIN_PX = 50;
    // Hauteur virtuelle du cadre de vue employée par setViewOffset : sert de
    // référence exacte partout où CAMERA_FOV convertit un angle en pixels
    // (cadrage par défaut, taille des points à l'écran).
    let currentFullHeight = window.innerHeight;

    function computeSafeZone() {
      const headerBottom = document.querySelector('header').getBoundingClientRect().bottom;
      const footerTop = document.querySelector('footer').getBoundingClientRect().top;
      const hintBar = document.getElementById('sillage-hint-bar');
      const hintBottom = (hintBar && !hintBar.hidden)
        ? hintBar.getBoundingClientRect().bottom
        : headerBottom;
      return {
        top: Math.max(headerBottom, hintBottom) + SAFE_ZONE_GAP_PX,
        bottom: footerTop - SAFE_ZONE_GAP_PX,
      };
    }

    function applyViewOffset() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const { top, bottom } = computeSafeZone();
      const shiftDown = (top + bottom) / 2 - h / 2;
      const fullHeight = h + Math.abs(shiftDown) * 2;
      const offsetY = shiftDown >= 0 ? 0 : fullHeight - h;
      camera.setViewOffset(w, fullHeight, 0, offsetY, w, h);
      camera.updateProjectionMatrix();
      currentFullHeight = fullHeight;
    }
    applyViewOffset();
    scene.fog = new THREE.Fog(0x000000, 4, 16);

    const pivotGroup = new THREE.Group();
    scene.add(pivotGroup);

    // Filaire de LA sphère (un seul maillage, continu) : par défaut, seuls
    // les fragments proches d'un point/amas de dépôts restent visibles (le
    // reste est découpé dans le fragment shader) — le bouton globe bascule
    // vers le maillage entier révélé. Légèrement plus petit que
    // SPHERE_RADIUS (rayon des points/courbes) pour que les courbes de
    // déambulation semblent flotter juste au-dessus.
    // Deux canaux de révélation : sous les points/amas (halo assez large,
    // bien visible) et le long des traits de déambulation (tube plus étroit,
    // plus discret) — pour laisser deviner le trajet entre deux dépôts
    // éloignés (ex. France → Canada), pas seulement leurs deux extrémités.
    const GLOBE_REVEAL_ANGLE_RAD = THREE.MathUtils.degToRad(12);
    const GLOBE_REVEAL_LINE_ANGLE_RAD = THREE.MathUtils.degToRad(8);
    // Sous-échantillonnage AVANT calcul du masque (voir updateRevealTexture
    // plus bas) : purement une histoire de budget CPU pour la cuisson de la
    // texture (nombre de dépôts/points de courbe × texels), pas une
    // limitation du rendu -- contrairement à l'ancien système par uniformes
    // (GLOBE_REVEAL_MAX_POINTS/LINE_POINTS), la sphère ANCHORS/couleurs des
    // punchlines elles-mêmes n'est pas concernée, seul l'effet visuel de
    // "halo autour des dépôts proches" peut légèrement s'approximer au-delà
    // de ces plafonds (déjà le cas avant, avec un plafond bien plus bas).
    const GLOBE_REVEAL_BAKE_MAX_POINTS = 150;
    const GLOBE_REVEAL_BAKE_MAX_LINE_POINTS = 200;
    // Géométrie low-poly à facettes irrégulières : icosaèdre subdivisé, puis
    // chaque sommet déplacé tangentiellement d'un petit décalage aléatoire
    // (les sommets partagés entre faces reçoivent le même décalage, sinon le
    // maillage se fissurerait visuellement), avant d'être replaqué sur la
    // sphère — casse la régularité de la grille sans jamais s'en écarter.
    const WORLD_UP = new THREE.Vector3(0, 1, 0);
    function buildIrregularGlobeGeometry(radius, detail, jitterAmount) {
      const geometry = new THREE.IcosahedronGeometry(radius, detail);
      const posAttr = geometry.attributes.position;
      const offsetByVertexKey = new Map();
      const v = new THREE.Vector3();
      const tangentA = new THREE.Vector3();
      const tangentB = new THREE.Vector3();
      for (let i = 0; i < posAttr.count; i++) {
        v.fromBufferAttribute(posAttr, i);
        const key = `${v.x.toFixed(4)}|${v.y.toFixed(4)}|${v.z.toFixed(4)}`;
        let offset = offsetByVertexKey.get(key);
        if (!offset) {
          const dir = v.clone().normalize();
          tangentA.crossVectors(dir, WORLD_UP);
          if (tangentA.lengthSq() < 1e-6) tangentA.crossVectors(dir, new THREE.Vector3(1, 0, 0));
          tangentA.normalize();
          tangentB.crossVectors(dir, tangentA).normalize();
          const angle = Math.random() * Math.PI * 2;
          const magnitude = Math.random() * jitterAmount;
          offset = tangentA.clone().multiplyScalar(Math.cos(angle) * magnitude)
            .addScaledVector(tangentB, Math.sin(angle) * magnitude);
          offsetByVertexKey.set(key, offset);
        }
        v.add(offset).setLength(radius);
        posAttr.setXYZ(i, v.x, v.y, v.z);
      }
      posAttr.needsUpdate = true;
      geometry.computeVertexNormals();
      return geometry;
    }
    const globeGeometry = buildIrregularGlobeGeometry(SPHERE_RADIUS * 0.95, 7, 0.25);

    // Masque de révélation précalculé en texture (équirectangulaire, R=
    // proximité aux points, G=proximité aux traits) -- remplace un système
    // antérieur où globeMaterial/coastlineMaterial/coastlineHaloMaterial/
    // atmosphereMaterial (dont l'atmosphère, qui couvre une grande partie de
    // l'écran) recalculaient CE MÊME résultat À CHAQUE PIXEL DE CHAQUE FRAME
    // via une boucle sur tous les points/traits (jusqu'à 64+160 itérations
    // trigonométriques par pixel) -- un coût GPU croissant avec le nombre de
    // punchlines, principale cause de ralentissement soutenu (pas juste au
    // chargement) constatée par Rudy. Recalculée uniquement quand les
    // dépôts changent (updateRevealTexture, appelée depuis buildScene), pas
    // à chaque frame : la résolution (256x128, puissance de 2 -- REQUIS en
    // WebGL1 pour RepeatWrapping, sans quoi le sampler se comporte de façon
    // indéfinie selon le driver -- observé sous forme de motif "papier
    // froissé" couvrant tout le globe au lieu de halos localisés autour des
    // dépôts) reste volontairement modeste puisque l'effet est de toute
    // façon un halo doux (adouci par smoothstep sur ~8-12°), le filtrage
    // bilinéaire suffit à le lisser.
    const REVEAL_TEX_W = 256;
    const REVEAL_TEX_H = 128;
    const revealTexCanvas = document.createElement('canvas');
    revealTexCanvas.width = REVEAL_TEX_W;
    revealTexCanvas.height = REVEAL_TEX_H;
    const revealTexCtx = revealTexCanvas.getContext('2d');
    const revealTexImageData = revealTexCtx.createImageData(REVEAL_TEX_W, REVEAL_TEX_H);
    const revealTexture = new THREE.CanvasTexture(revealTexCanvas);
    // THREE.CanvasTexture flip la texture verticalement par défaut
    // (convention WebGL, v=0 en bas) -- le canvas/ImageData a lui la ligne 0
    // en haut (v proportionnel à phi/π depuis le pôle nord dans le bake ET
    // le sampling, cf. updateRevealTexture/revealUV()). Sans ce flag, nord
    // et sud sont inversés à l'affichage (constaté : révélait l'Amérique du
    // Sud/l'Afrique au lieu des dépôts, tous au nord) -- même correctif déjà
    // en place sur buildCleanedMaskTexture plus bas, pour la même raison.
    revealTexture.flipY = false;
    revealTexture.wrapS = THREE.RepeatWrapping;
    revealTexture.wrapT = THREE.ClampToEdgeWrapping;
    revealTexture.minFilter = THREE.LinearFilter;
    revealTexture.magFilter = THREE.LinearFilter;
    revealTexture.generateMipmaps = false;

    // Uniformes de révélation (points/traits/globe entier) du filaire M1.
    const revealUniforms = {
      uColor: { value: new THREE.Color(0xffffff) },
      uPointOpacity: { value: 0.25 },
      uLineOpacity: { value: 0.25 },
      uFullOpacity: { value: 0.25 },
      uRevealAll: { value: false },
      // Révélation "autour des publications" (bouton demi-globe) -- séparée
      // de uRevealAll (bouton globe plein) : les deux sont mutuellement
      // exclusifs (cf. leurs listeners plus bas), et au chargement de la
      // page NI L'UN NI L'AUTRE n'est actif -- le globe/littoral/halo/
      // atmosphère restent invisibles tant que l'un des deux boutons n'a pas
      // été cliqué (demande Rudy : seuls les points/traits/sphère des
      // alphabets apparaissent au chargement).
      uProximityReveal: { value: false },
      uRevealTex: { value: revealTexture },
      // Halo fresnel léger (bord du globe M1 / contour littoral).
      uRimColor: { value: new THREE.Color(0xffffff) },
      uRimIntensity: { value: 1.0 },
      uRimPower: { value: 1.4 },
    };
    const REVEAL_VERTEX_SHADER = `
      varying vec3 vDir;
      varying vec3 vWorldPos;
      varying vec3 vWorldDir;
      void main() {
        vDir = normalize(position);
        vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        vWorldDir = normalize(mat3(modelMatrix) * position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    const REVEAL_FRAGMENT_BODY = `
      uniform vec3 uColor;
      uniform float uPointOpacity;
      uniform float uLineOpacity;
      uniform float uFullOpacity;
      uniform bool uRevealAll;
      uniform bool uProximityReveal;
      uniform sampler2D uRevealTex;
      uniform vec3 uRimColor;
      uniform float uRimIntensity;
      uniform float uRimPower;
      varying vec3 vDir;
      varying vec3 vWorldPos;
      varying vec3 vWorldDir;

      // Halo littoral : plus fort quand la surface fait face à la caméra
      // (continents au premier plan, donc les plus lisibles), et s'atténue
      // vers le bord du globe — l'inverse d'un fresnel de bord classique.
      float rimFresnel() {
        vec3 viewDir = normalize(cameraPosition - vWorldPos);
        float facing = clamp(dot(normalize(vWorldDir), viewDir), 0.0, 1.0);
        return pow(facing, uRimPower);
      }

      // Coordonnées équirectangulaires (même convention que isOceanFrag plus
      // bas / dirToUV côté JS) -- utilisées pour lire uRevealTex, le masque
      // de révélation précalculé (voir updateRevealTexture) : R = proximité
      // aux points, G = proximité aux traits, déjà adoucis (smoothstep) et
      // déjà bornés par leur angle de révélation au moment de la cuisson.
      vec2 revealUV() {
        vec3 d = normalize(vDir);
        float phi = acos(clamp(d.y, -1.0, 1.0));
        float theta = atan(d.z, -d.x);
        float u = theta / (2.0 * 3.14159265);
        u = u - floor(u);
        return vec2(u, phi / 3.14159265);
      }

      float revealAlpha() {
        if (uRevealAll) return uFullOpacity;
        if (!uProximityReveal) return 0.0;
        vec2 mask = texture2D(uRevealTex, revealUV()).rg;
        return max(uPointOpacity * mask.r, uLineOpacity * mask.g);
      }

      // Même logique que revealAlpha(), mais normalisée en 0..1 (sans les
      // opacités de base uPointOpacity/uLineOpacity) : sert de facteur de
      // révélation pour des effets qui ont leur propre intensité, comme le
      // halo atmosphérique.
      float revealMask() {
        if (uRevealAll) return 1.0;
        if (!uProximityReveal) return 0.0;
        vec2 mask = texture2D(uRevealTex, revealUV()).rg;
        return max(mask.r, mask.g);
      }
    `;

    // M2 (masque terre/océan, jamais affiché pour lui-même) : carte
    // spéculaire terrestre (NASA, texture d'exemple three.js — terre en
    // noir, océan en blanc). Nettoyée par étiquetage de composantes
    // connexes (parcours en largeur, avec repli horizontal puisque la
    // longitude boucle) : les petites îles isolées sous un seuil de surface
    // sont retirées, et toute étendue d'eau qui ne touche pas le plus gros
    // bassin océanique (donc un lac à l'intérieur des terres) est comblée.
    function sampleLandOceanMask(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0);
          const { data } = ctx.getImageData(0, 0, img.width, img.height);
          resolve({ data, width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = url;
      });
    }
    const dirToUVTmp = new THREE.Vector3();
    function dirToUV(dir) {
      // dir peut être un sommet à pleine échelle (rayon du globe) plutôt
      // qu'une direction unitaire — toujours renormaliser ici, sans quoi
      // acos(clamp(y)) sature à un pôle pour la quasi-totalité des points.
      const d = dirToUVTmp.copy(dir).normalize();
      const phi = Math.acos(THREE.MathUtils.clamp(d.y, -1, 1));
      const theta = Math.atan2(d.z, -d.x);
      let u = theta / (2 * Math.PI);
      u -= Math.floor(u);
      return { u, v: phi / Math.PI };
    }
    function uvToDir(u, v) {
      const phi = v * Math.PI;
      const theta = u * Math.PI * 2;
      return new THREE.Vector3(
        -Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta),
      );
    }
    const ISLAND_MIN_AREA_PX = 12;
    // En dessous de ce seuil, une île garde son contour (désormais assez
    // précis pour la tracer correctement) mais ne reçoit pas de croix de
    // réparation : sur une forme aussi minuscule, la croix — dimensionnée
    // pour rester visible — dépasse presque toujours son propre contour.
    const REPAIR_MARK_MIN_AREA_PX = 150;
    // Une petite île (ou tout autre territoire plus étroit qu'une facette de
    // base) peut n'être traversée par aucun trait low-poly si aucune
    // facette n'a son centre dedans — juste son contour. On calcule ici, en
    // plus du nettoyage terre/océan, le centre et un rayon approximatif de
    // chaque petite terre gardée, pour pouvoir y ajouter un petit repère en
    // croix après coup si elle se retrouve sans aucune facette (cf.
    // buildLandOnlyWireframe).
    const SMALL_LAND_REPAIR_MAX_PX = 6000;
    function cleanLandOceanMask({ data, width, height }) {
      const n = width * height;
      const isLand = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        const o = i * 4;
        isLand[i] = (data[o] + data[o + 1] + data[o + 2] > 1.5 * 255) ? 0 : 1;
      }
      const label = new Int32Array(n).fill(-1);
      const queue = new Int32Array(n);
      const componentType = [];
      const componentSize = [];
      for (let start = 0; start < n; start++) {
        if (label[start] !== -1) continue;
        const type = isLand[start];
        const compId = componentType.length;
        let head = 0; let tail = 0;
        queue[tail++] = start;
        label[start] = compId;
        while (head < tail) {
          const cur = queue[head++];
          const x = cur % width;
          const y = (cur - x) / width;
          const left = y * width + (x === 0 ? width - 1 : x - 1);
          const right = y * width + (x === width - 1 ? 0 : x + 1);
          const up = y > 0 ? cur - width : -1;
          const down = y < height - 1 ? cur + width : -1;
          if (label[left] === -1 && isLand[left] === type) { label[left] = compId; queue[tail++] = left; }
          if (label[right] === -1 && isLand[right] === type) { label[right] = compId; queue[tail++] = right; }
          if (up >= 0 && label[up] === -1 && isLand[up] === type) { label[up] = compId; queue[tail++] = up; }
          if (down >= 0 && label[down] === -1 && isLand[down] === type) { label[down] = compId; queue[tail++] = down; }
        }
        componentType.push(type);
        componentSize.push(tail);
      }
      let mainOceanId = -1; let mainOceanSize = -1;
      for (let c = 0; c < componentType.length; c++) {
        if (componentType[c] === 0 && componentSize[c] > mainOceanSize) {
          mainOceanSize = componentSize[c];
          mainOceanId = c;
        }
      }
      const land = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        const compId = label[i];
        land[i] = componentType[compId] === 1
          ? (componentSize[compId] >= ISLAND_MIN_AREA_PX ? 1 : 0)
          : (compId === mainOceanId ? 0 : 1);
      }
      // Une micro-île de terre (bruit de texture, souvent 1 pixel) retirée
      // ci-dessus retombe à 0 (océan) sans tenir compte de son voisinage :
      // si elle se trouvait au milieu d'un lac déjà comblé, ça laisse un
      // pixel d'océan isolé qui dessine un petit tracé parasite au rendu
      // (visible par ex. sur de grands lacs en Russie/Amérique du Nord).
      // Seconde passe, même logique que le comblement des lacs : tout pixel
      // "océan" du résultat qui ne rejoint pas le plus grand bassin par une
      // chaîne de voisins océan est rebouché en terre.
      const label2 = new Int32Array(n).fill(-1);
      const componentSize2 = [];
      for (let start = 0; start < n; start++) {
        if (land[start] !== 0 || label2[start] !== -1) continue;
        const compId = componentSize2.length;
        let head = 0; let tail = 0;
        queue[tail++] = start;
        label2[start] = compId;
        while (head < tail) {
          const cur = queue[head++];
          const x = cur % width;
          const y = (cur - x) / width;
          const left = y * width + (x === 0 ? width - 1 : x - 1);
          const right = y * width + (x === width - 1 ? 0 : x + 1);
          const up = y > 0 ? cur - width : -1;
          const down = y < height - 1 ? cur + width : -1;
          if (land[left] === 0 && label2[left] === -1) { label2[left] = compId; queue[tail++] = left; }
          if (land[right] === 0 && label2[right] === -1) { label2[right] = compId; queue[tail++] = right; }
          if (up >= 0 && land[up] === 0 && label2[up] === -1) { label2[up] = compId; queue[tail++] = up; }
          if (down >= 0 && land[down] === 0 && label2[down] === -1) { label2[down] = compId; queue[tail++] = down; }
        }
        componentSize2.push(tail);
      }
      let mainOceanId2 = -1; let mainOceanSize2 = -1;
      for (let c = 0; c < componentSize2.length; c++) {
        if (componentSize2[c] > mainOceanSize2) { mainOceanSize2 = componentSize2[c]; mainOceanId2 = c; }
      }
      for (let i = 0; i < n; i++) {
        if (land[i] === 0 && label2[i] !== mainOceanId2) land[i] = 1;
      }
      // Centre (direction moyenne) et rayon approximatif des petites terres
      // gardées, pour le repêchage d'îles sans facette (cf. plus bas).
      const smallLandInfo = new Map();
      for (let i = 0; i < n; i++) {
        const compId = label[i];
        if (componentType[compId] !== 1) continue;
        const size = componentSize[compId];
        if (size < REPAIR_MARK_MIN_AREA_PX || size > SMALL_LAND_REPAIR_MAX_PX) continue;
        let info = smallLandInfo.get(compId);
        if (!info) {
          info = { sum: new THREE.Vector3(), size };
          smallLandInfo.set(compId, info);
        }
        const x = i % width;
        const y = (i - x) / width;
        info.sum.add(uvToDir((x + 0.5) / width, (y + 0.5) / height));
      }
      for (const info of smallLandInfo.values()) {
        info.dir = info.sum.normalize();
        info.angularRadius = Math.sqrt(info.size / Math.PI) / width * Math.PI * 2;
      }
      return {
        land, label, width, height, smallLandInfo,
      };
    }
    function pixelIndexAtUV(u, v, cleaned) {
      const px = Math.min(cleaned.width - 1, Math.max(0, Math.floor(u * cleaned.width)));
      const py = Math.min(cleaned.height - 1, Math.max(0, Math.floor(v * cleaned.height)));
      return py * cleaned.width + px;
    }
    function isOceanAtUV(u, v, cleaned) {
      return cleaned.land[pixelIndexAtUV(u, v, cleaned)] === 0;
    }
    // Reconstruit le masque nettoyé en canvas noir/blanc pour pouvoir aussi
    // le lire côté GPU (fragment shader), avec exactement la même donnée
    // que le filtrage/tracé côté CPU ci-dessous.
    function buildCleanedMaskTexture(cleaned) {
      const canvas = document.createElement('canvas');
      canvas.width = cleaned.width;
      canvas.height = cleaned.height;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.createImageData(cleaned.width, cleaned.height);
      for (let i = 0; i < cleaned.land.length; i++) {
        const v = cleaned.land[i] ? 0 : 255; // terre = noir, océan = blanc
        const o = i * 4;
        imageData.data[o] = v; imageData.data[o + 1] = v; imageData.data[o + 2] = v; imageData.data[o + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
      const texture = new THREE.CanvasTexture(canvas);
      texture.flipY = false;
      // Filtrage au plus proche (pas de lissage bilinéaire ni de mipmaps) :
      // la découpe GPU du filaire (fragment shader) doit tomber pile sur le
      // même pixel que le calcul CPU (contour, choix des facettes), sinon
      // le lissage flouterait la frontière terre/océan et les deux
      // tracés se décaleraient l'un par rapport à l'autre.
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      return texture;
    }
    // Petit repère en croix (2 traits perpendiculaires) centré sur une
    // petite terre que le maillage de base a manquée — sa taille suit le
    // rayon approximatif de la terre en question. Peut légèrement déborder
    // en mer sur les formes allongées (Cuba, la Basse-Californie...) : sans
    // conséquence, le fragment shader découpe déjà tout ce qui tombe dans
    // l'océan (cf. globeMaterial).
    const REPAIR_WORLD_UP = new THREE.Vector3(0, 1, 0);
    function addOrphanLandMark(dir, angularRadius, radius, kept) {
      const d = dir.clone().normalize();
      const tangentA = new THREE.Vector3().crossVectors(d, REPAIR_WORLD_UP);
      if (tangentA.lengthSq() < 1e-6) tangentA.crossVectors(d, new THREE.Vector3(1, 0, 0));
      tangentA.normalize();
      const tangentB = new THREE.Vector3().crossVectors(d, tangentA).normalize();
      const half = Math.max(angularRadius * 1.3, THREE.MathUtils.degToRad(0.6));
      const pointAt = (angle) => d.clone()
        .addScaledVector(tangentA, Math.cos(angle) * half)
        .addScaledVector(tangentB, Math.sin(angle) * half)
        .normalize()
        .multiplyScalar(radius);
      const p1 = pointAt(0);
      const p2 = pointAt(Math.PI);
      const p3 = pointAt(Math.PI / 2);
      const p4 = pointAt(-Math.PI / 2);
      kept.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
      kept.push(p3.x, p3.y, p3.z, p4.x, p4.y, p4.z);
    }
    // Ne garde que les arêtes des facettes terrestres (classées par leur
    // centre) — chaque arête est donc soit entièrement tracée, soit
    // entièrement absente, jamais coupée en plein milieu. Les petites
    // terres qu'aucune facette n'a touchées (plus étroites que le grain du
    // maillage — Corse, Cuba, Basse-Californie...) reçoivent en plus un
    // petit repère en croix, pour qu'on les distingue de leur seul contour.
    function buildLandOnlyWireframe(geometry, cleaned, radius) {
      const pos = geometry.attributes.position;
      const kept = [];
      const va = new THREE.Vector3(); const vb = new THREE.Vector3(); const vc = new THREE.Vector3();
      const coveredComponents = new Set();
      // Un trait étroit du continent (pointe, isthme, péninsule) peut être
      // plus fin qu'une facette de base : si on ne testait que le centre de
      // la facette, elle tomberait entièrement en mer même si un de ses
      // sommets touche la terre. On garde donc la facette dès qu'AU MOINS
      // UN sommet est terrestre — l'éventuel débordement en mer est de toute
      // façon découpé au pixel près par le fragment shader (globeMaterial).
      for (let f = 0; f < pos.count / 3; f++) {
        va.fromBufferAttribute(pos, f * 3);
        vb.fromBufferAttribute(pos, f * 3 + 1);
        vc.fromBufferAttribute(pos, f * 3 + 2);
        const uvA = dirToUV(va); const uvB = dirToUV(vb); const uvC = dirToUV(vc);
        const landA = !isOceanAtUV(uvA.u, uvA.v, cleaned);
        const landB = !isOceanAtUV(uvB.u, uvB.v, cleaned);
        const landC = !isOceanAtUV(uvC.u, uvC.v, cleaned);
        if (!landA && !landB && !landC) continue;
        if (landA) coveredComponents.add(cleaned.label[pixelIndexAtUV(uvA.u, uvA.v, cleaned)]);
        if (landB) coveredComponents.add(cleaned.label[pixelIndexAtUV(uvB.u, uvB.v, cleaned)]);
        if (landC) coveredComponents.add(cleaned.label[pixelIndexAtUV(uvC.u, uvC.v, cleaned)]);
        kept.push(
          va.x, va.y, va.z, vb.x, vb.y, vb.z,
          vb.x, vb.y, vb.z, vc.x, vc.y, vc.z,
          vc.x, vc.y, vc.z, va.x, va.y, va.z,
        );
      }
      for (const [compId, info] of cleaned.smallLandInfo) {
        if (coveredComponents.has(compId)) continue;
        addOrphanLandMark(info.dir, info.angularRadius, radius, kept);
      }
      const landGeometry = new THREE.BufferGeometry();
      landGeometry.setAttribute('position', new THREE.Float32BufferAttribute(kept, 3));
      return landGeometry;
    }
    // Contour littoral (marching squares sur le masque nettoyé) : trace la
    // vraie limite terre/mer indépendamment du grain des facettes low-poly
    // — referme visuellement ce que leur grain laisse trop anguleux. Grille
    // régulière en (u, v), un segment par arête de cellule séparant un coin
    // terre d'un coin océan (table à 16 cas standard).
    // Résolution alignée sur celle du masque nettoyé (cf. sampleLandOceanMask)
    // pour que le contour puisse tracer toute île qui survit au filtrage —
    // une grille plus grossière raterait de petites terres qui influencent
    // quand même le choix des facettes et la découpe GPU, laissant des
    // formes flottantes sans aucun tracé autour.
    const COASTLINE_GRID_COLS = 2048;
    const COASTLINE_GRID_ROWS = 1024;
    const COASTLINE_CASES = {
      1: [['left', 'bottom']],
      2: [['bottom', 'right']],
      3: [['left', 'right']],
      4: [['top', 'right']],
      5: [['top', 'left'], ['bottom', 'right']],
      6: [['top', 'bottom']],
      7: [['top', 'left']],
      8: [['top', 'left']],
      9: [['top', 'bottom']],
      10: [['top', 'right'], ['bottom', 'left']],
      11: [['top', 'right']],
      12: [['left', 'right']],
      13: [['bottom', 'right']],
      14: [['left', 'bottom']],
    };
    // cols/rows par défaut = résolution fine du masque (tracé littoral
    // précis). Le halo diffus (cf. plus bas) reconstruit ce même contour à
    // une résolution bien plus grossière : des segments plus longs évitent
    // le moiré/hatching qu'un ruban large produirait sur des micro-segments
    // juxtaposés à angles légèrement différents.
    function buildCoastlineContour(cleaned, radius, cols = COASTLINE_GRID_COLS, rows = COASTLINE_GRID_ROWS) {
      const kept = [];
      for (let j = 0; j < rows; j++) {
        const v0 = j / rows;
        const v1 = (j + 1) / rows;
        for (let i = 0; i < cols; i++) {
          const u0 = i / cols;
          const u1 = (i + 1) / cols;
          const tl = isOceanAtUV(u0, v0, cleaned) ? 1 : 0;
          const tr = isOceanAtUV(u1, v0, cleaned) ? 1 : 0;
          const br = isOceanAtUV(u1, v1, cleaned) ? 1 : 0;
          const bl = isOceanAtUV(u0, v1, cleaned) ? 1 : 0;
          const caseIndex = (tl << 3) | (tr << 2) | (br << 1) | bl;
          const segments = COASTLINE_CASES[caseIndex];
          if (!segments) continue;
          const uMid = (u0 + u1) / 2;
          const vMid = (v0 + v1) / 2;
          const edgePoint = {
            top: [uMid, v0],
            bottom: [uMid, v1],
            left: [u0, vMid],
            right: [u1, vMid],
          };
          for (const [a, b] of segments) {
            const pa = uvToDir(...edgePoint[a]).multiplyScalar(radius);
            const pb = uvToDir(...edgePoint[b]).multiplyScalar(radius);
            kept.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
          }
        }
      }
      const contourGeometry = new THREE.BufferGeometry();
      contourGeometry.setAttribute('position', new THREE.Float32BufferAttribute(kept, 3));
      return contourGeometry;
    }

    // Le filaire low-poly (facettes gardées entières par leur centre) peut
    // légèrement déborder la côte réelle sur les arêtes des facettes
    // frontalières — on découpe donc en plus, pixel par pixel, tout ce qui
    // tombe dans l'océan d'après le masque nettoyé, pour ne jamais dépasser
    // le contour littoral tracé par-dessus.
    const globeMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { ...revealUniforms, uEarthTexture: { value: null } },
      vertexShader: REVEAL_VERTEX_SHADER,
      fragmentShader: `
        uniform sampler2D uEarthTexture;
        ${REVEAL_FRAGMENT_BODY}
        bool isOceanFrag(vec3 dir) {
          // vDir arrive interpolé linéairement entre les deux extrémités du
          // segment (unitaires chacune, mais leur mélange ne l'est plus) :
          // sur une longue arête traversant un océan, ne pas renormaliser
          // ici décale le point échantillonné du vrai grand cercle et peut
          // faire retomber sur un pixel "terre" du masque à tort, laissant
          // le trait déborder sans être découpé.
          vec3 d = normalize(dir);
          float phi = acos(clamp(d.y, -1.0, 1.0));
          float theta = atan(d.z, -d.x);
          float u = theta / (2.0 * 3.14159265);
          u = u - floor(u);
          vec3 c = texture2D(uEarthTexture, vec2(u, phi / 3.14159265)).rgb;
          return (c.r + c.g + c.b) > 1.5;
        }
        // Ce maillage (continents, "M1") ne s'affiche jamais sous "reveal
        // all" (bouton globe) -- seul le remplissage noir (landFillMesh) et
        // le contour littoral (coastlineMesh) y sont visibles. Reste affiché
        // normalement sous "reveal near" (demande Rudy).
        void main() {
          if (!uProximityReveal) discard;
          if (isOceanFrag(vDir)) discard;
          vec2 mask = texture2D(uRevealTex, revealUV()).rg;
          float alpha = max(uPointOpacity * mask.r, uLineOpacity * mask.g);
          if (alpha <= 0.0) discard;
          gl_FragColor = vec4(uColor, alpha);
        }
      `,
    });
    const globeMesh = new THREE.LineSegments(new THREE.BufferGeometry(), globeMaterial);
    // Désactivé (demande Rudy, garde le remplissage noir + dégradé de
    // landFillMesh comme seul rendu de "terre révélée") -- juste .visible,
    // pas supprimé : tout le réglage (découpe océan pixel-perfect, etc.)
    // reste en l'état pour pouvoir le rallumer facilement plus tard.
    globeMesh.visible = false;
    pivotGroup.add(globeMesh);

    // Le contour littoral est déjà exactement sur la limite terre/océan
    // (calculé depuis le même masque) : pas besoin d'y répéter la découpe.
    const coastlineMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { ...revealUniforms },
      vertexShader: REVEAL_VERTEX_SHADER,
      fragmentShader: `
        ${REVEAL_FRAGMENT_BODY}
        void main() {
          float alpha = revealAlpha();
          if (alpha <= 0.0) discard;
          float rim = rimFresnel() * uRimIntensity;
          vec3 color = mix(uColor, uRimColor, clamp(rim * 2.0, 0.0, 1.0));
          gl_FragColor = vec4(color, min(1.0, alpha + rim));
        }
      `,
    });
    const coastlineMesh = new THREE.LineSegments(new THREE.BufferGeometry(), coastlineMaterial);
    // Désactivé (demande Rudy, même raison que globeMesh ci-dessus) -- juste
    // .visible, réglage conservé pour pouvoir le rallumer facilement.
    coastlineMesh.visible = false;
    pivotGroup.add(coastlineMesh);

    // Halo diffus du littoral : simple doublon du tracé littoral exact
    // (même géométrie que coastlineMesh, reconstruite à l'identique à
    // chaque fois) — un ruban large — même technique "fat line" que
    // fatLineMaterial plus bas — construit SYMÉTRIQUEMENT de part et
    // d'autre du tracé (donc flou aussi bien côté terre que côté mer), puis
    // masqué au rendu par le masque terre/océan (uEarthTexture, déjà utilisé
    // par globeMaterial) pour ne laisser subsister le flou que là où il
    // tombe réellement dans l'océan. Le tracé net (coastlineMaterial,
    // au-dessus) sert visuellement de démarcation nette sur le bord terre.
    const COASTLINE_HALO_WIDTH_PX = 10;
    const coastlineHaloMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        ...revealUniforms,
        uEarthTexture: { value: null },
        resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        haloWidth: { value: COASTLINE_HALO_WIDTH_PX },
        uHaloColor: { value: new THREE.Color(0xffffff) },
        uHaloIntensity: { value: 0.26 },
      },
      vertexShader: `
        uniform vec2 resolution;
        uniform float haloWidth;
        attribute vec3 segStart;
        attribute vec3 segEnd;
        attribute float side;
        varying vec3 vDir;
        varying vec3 vWorldPos;
        varying vec3 vWorldDir;
        varying float vLateral;
        void main() {
          vDir = normalize(position);
          vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
          vWorldDir = normalize(mat3(modelMatrix) * position);
          vLateral = side;

          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vec4 clip = projectionMatrix * mvPosition;
          vec2 screenPos = (clip.xy / clip.w) * resolution * 0.5;

          // Test : étirement à sens unique vers l'extérieur du globe (comme
          // de la lumière qui s'échappe du littoral vers l'espace), toujours
          // centré sur le tracé — side va de 0 (sur le trait) à 1 (bord
          // extérieur). Le centre du globe (pivotGroup ne fait que tourner,
          // jamais translater : son origine reste (0,0,0) en espace monde)
          // projeté à l'écran donne la direction radiale par sommet.
          vec4 centerClip = projectionMatrix * modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          vec2 screenCenter = (centerClip.xy / centerClip.w) * resolution * 0.5;
          vec2 radial = screenPos - screenCenter;
          vec2 normal = (length(radial) < 0.0001) ? vec2(0.0, 1.0) : normalize(radial);
          vec2 pixelOffset = normal * side * haloWidth;
          vec2 ndcOffset = pixelOffset / (resolution * 0.5);

          gl_Position = clip;
          gl_Position.xy += ndcOffset * clip.w;
        }
      `,
      fragmentShader: `
        ${REVEAL_FRAGMENT_BODY}
        uniform sampler2D uEarthTexture;
        uniform vec3 uHaloColor;
        uniform float uHaloIntensity;
        varying float vLateral;
        bool isOceanFrag(vec3 dir) {
          vec3 d = normalize(dir);
          float phi = acos(clamp(d.y, -1.0, 1.0));
          float theta = atan(d.z, -d.x);
          float u = theta / (2.0 * 3.14159265);
          u = u - floor(u);
          vec3 c = texture2D(uEarthTexture, vec2(u, phi / 3.14159265)).rgb;
          return (c.r + c.g + c.b) > 1.5;
        }
        void main() {
          // Masqué par le masque terre/océan : le ruban est construit des
          // deux côtés du tracé, mais on ne garde que ce qui tombe dans
          // l'océan — le côté terre disparaît, sans logique de direction à
          // calculer.
          if (!isOceanFrag(vDir)) discard;
          float mask = revealMask();
          if (mask <= 0.0) discard;
          float lateralFalloff = pow(1.0 - clamp(vLateral, 0.0, 1.0), 2.0);
          float haloAlpha = clamp(uHaloIntensity * lateralFalloff * mask, 0.0, 1.0);
          gl_FragColor = vec4(uHaloColor, haloAlpha);
        }
      `,
    });
    function buildFatLineSegmentsGeometry(srcGeometry) {
      const src = srcGeometry.attributes.position.array;
      const segCount = src.length / 6;
      const positions = [];
      const segStarts = [];
      const segEnds = [];
      const sides = [];
      const indices = [];
      let vertIndex = 0;
      for (let seg = 0; seg < segCount; seg++) {
        const ai = seg * 6;
        const bi = ai + 3;
        const ax = src[ai]; const ay = src[ai + 1]; const az = src[ai + 2];
        const bx = src[bi]; const by = src[bi + 1]; const bz = src[bi + 2];
        positions.push(ax, ay, az, ax, ay, az, bx, by, bz, bx, by, bz);
        segStarts.push(ax, ay, az, ax, ay, az, ax, ay, az, ax, ay, az);
        segEnds.push(bx, by, bz, bx, by, bz, bx, by, bz, bx, by, bz);
        // 0 = sur le trait, 1 = bord supérieur (étirement à sens unique).
        sides.push(0, 1, 0, 1);
        indices.push(vertIndex, vertIndex + 2, vertIndex + 1, vertIndex + 2, vertIndex + 3, vertIndex + 1);
        vertIndex += 4;
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('segStart', new THREE.Float32BufferAttribute(segStarts, 3));
      geometry.setAttribute('segEnd', new THREE.Float32BufferAttribute(segEnds, 3));
      geometry.setAttribute('side', new THREE.Float32BufferAttribute(sides, 1));
      geometry.setIndex(indices);
      return geometry;
    }
    const coastlineHaloMesh = new THREE.Mesh(new THREE.BufferGeometry(), coastlineHaloMaterial);
    // Désactivé avec coastlineMesh (c'est son halo, inutile sans le tracé net
    // qu'il accompagne) -- même raison, réglage conservé.
    coastlineHaloMesh.visible = false;
    pivotGroup.add(coastlineHaloMesh);

    // Halo atmosphérique : coque à la taille du maillage, rendue face avant
    // en fondu additif — contrairement au fresnel sur les lignes, celui-ci
    // déborde visuellement du contour du globe (effet "corona" recherché).
    // Soumis au même système de révélation que le filaire : visible sous
    // les points/traits, ou entièrement via le bouton globe (uRevealAll).
    const atmosphereMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
      uniforms: {
        ...revealUniforms,
        uAtmosphereColor: { value: new THREE.Color(0xffffff) },
        uAtmosphereIntensity: { value: 0.85 },
        uAtmospherePower: { value: 3.0 },
      },
      vertexShader: `
        varying vec3 vDir;
        varying vec3 vWorldPos;
        varying vec3 vWorldDir;
        varying vec3 vNormalView;
        void main() {
          vDir = normalize(position);
          vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
          vWorldDir = normalize(mat3(modelMatrix) * position);
          vNormalView = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        ${REVEAL_FRAGMENT_BODY}
        uniform vec3 uAtmosphereColor;
        uniform float uAtmosphereIntensity;
        uniform float uAtmospherePower;
        varying vec3 vNormalView;
        void main() {
          float mask = revealMask();
          if (mask <= 0.0) discard;
          float facing = clamp(dot(vNormalView, vec3(0.0, 0.0, 1.0)), 0.0, 1.0);
          float rim = pow(1.0 - facing, uAtmospherePower);
          gl_FragColor = vec4(uAtmosphereColor, rim * uAtmosphereIntensity * mask);
        }
      `,
    });
    const atmosphereMesh = new THREE.Mesh(
      new THREE.SphereGeometry(SPHERE_RADIUS * 0.95, 48, 48),
      atmosphereMaterial
    );
    // Désactivé temporairement (test de Rudy : voir si le remplissage noir
    // des continents suffit à lire la planète sans le halo) -- juste
    // .visible, réglage conservé pour repasser à true facilement.
    atmosphereMesh.visible = false;
    pivotGroup.add(atmosphereMesh);

    // Remplissage des continents en noir (demande Rudy, approche plus simple
    // que le masquage par particule essayé précédemment -- coque pleine à
    // l'échelle du globe plutôt qu'un calcul par particule de la sphère des
    // alphabets, qui dérivait avec sa propre rotation). "Reveal all" :
    // rempli partout, sans dégradé. "Reveal near" (proximité) : réutilise
    // directement revealMask() -- même texture, même dégradé doux aux bords
    // que le maillage/littoral. Rayon légèrement en retrait (0.94 contre
    // 0.95) pour rester nettement DERRIÈRE le maillage/littoral et ne
    // jamais les couvrir (via renderOrder plus bas, pas seulement le rayon).
    const landFillMaterial = new THREE.ShaderMaterial({
      transparent: true,
      // true (pas false comme les autres coques du globe) : un remplissage
      // "plein" doit vraiment occulter ce qu'il y a derrière (y compris la
      // moitié arrière du globe, sinon elle se voit par transparence à
      // travers le remplissage de la moitié avant), contrairement à un
      // simple filaire/halo qui reste volontairement discret.
      depthWrite: true,
      uniforms: { ...revealUniforms, uEarthTexture: { value: null } },
      vertexShader: REVEAL_VERTEX_SHADER,
      fragmentShader: `
        uniform sampler2D uEarthTexture;
        ${REVEAL_FRAGMENT_BODY}
        bool isOceanFrag(vec3 dir) {
          vec3 d = normalize(dir);
          float phi = acos(clamp(d.y, -1.0, 1.0));
          float theta = atan(d.z, -d.x);
          float u = theta / (2.0 * 3.14159265);
          u = u - floor(u);
          vec3 c = texture2D(uEarthTexture, vec2(u, phi / 3.14159265)).rgb;
          return (c.r + c.g + c.b) > 1.5;
        }
        void main() {
          if (isOceanFrag(vDir)) discard;
          float mask = revealMask();
          if (mask <= 0.0) discard;
          gl_FragColor = vec4(0.0, 0.0, 0.0, mask);
        }
      `,
    });
    const landFillMesh = new THREE.Mesh(
      new THREE.SphereGeometry(SPHERE_RADIUS * 0.94, 48, 48),
      landFillMaterial
    );
    // Ordre de dessin explicite (pas seulement la distance caméra, ambiguë
    // ici puisque plusieurs coques partagent le même centre) : sphère des
    // alphabets (0, défaut) < remplissage noir (1) < maillage/littoral/halo/
    // atmosphère (2) < points/traits des dépôts (3, cf. pointMeshes/
    // lineMeshes plus bas) -- le remplissage recouvre bien la sphère, le
    // maillage reste au premier plan par-dessus, et les dépôts eux-mêmes ne
    // disparaissent jamais sous une zone révélée (demande Rudy).
    landFillMesh.renderOrder = 1;
    globeMesh.renderOrder = 2;
    coastlineMesh.renderOrder = 2;
    coastlineHaloMesh.renderOrder = 2;
    atmosphereMesh.renderOrder = 2;
    pivotGroup.add(landFillMesh);

    // ── Sphère des alphabets ─────────────────────────────────────────────
    // Imbriquée au centre du globe, à DEFAULT_SPHERE_SETTINGS.scale (85% par
    // défaut) du rayon -- ajoutée à pivotGroup comme le reste du globe, donc
    // hérite de sa rotation (glisser-déplacer) en plus de sa propre rotation
    // lente indépendante (cf. alphabetSphere.js). Construite tout de suite
    // avec les réglages par défaut pour être visible sans attendre le
    // réseau ; /api/sphere-settings puis les vraies punchlines viennent
    // l'affiner juste après (voir plus bas, section "Chargement des données").
    const alphabetSphere = createAlphabetSphere({ radius: SPHERE_RADIUS * 0.95 });
    pivotGroup.add(alphabetSphere.object3D);
    let lastAlphabetSphereTs = performance.now();

    // Évite le flash au chargement : points/traits colorés (dépôts) et
    // sphère des alphabets démarrent invisibles, puis apparaissent en fondu
    // dès que le masque terre/océan a fini de charger (sampleLandOceanMask
    // plus bas, mise à jour dans animate()) -- points/traits tout de suite,
    // sphère des alphabets une seconde plus tard (elle prend le relais).
    // Le globe (filaire/littoral/halo/atmosphère) n'a lui aucun fondu : il
    // reste invisible par défaut (aucun des deux modes de révélation actif,
    // cf. uRevealAll/uProximityReveal) tant que l'utilisateur ne clique pas
    // un des deux boutons -- demande Rudy, seuls points/traits/sphère
    // apparaissent au chargement.
    const POINTS_FADE_DURATION_MS = 1000;
    const SPHERE_FADE_DELAY_MS = 1000;
    const SPHERE_FADE_DURATION_MS = 1000;
    let pointsFadeStartTs = null;
    let sphereFadeStartTs = null;
    // Le fondu ne doit démarrer qu'une fois le masque terre/océan ET les
    // dépôts (donc le cadrage caméra final, cf. computeDefaultView plus bas)
    // prêts tous les deux -- sinon, sur un réseau mobile plus lent où le
    // masque (image locale) finit largement avant /api/generations, le
    // fondu se jouait sur des points déjà construits mais encore au
    // cadrage par défaut (radius=9, trop proche -- "premier plan"), puis la
    // caméra sautait sans transition au bon cadrage dès que les dépôts
    // arrivaient : deux effets visuels qui donnaient l'impression d'un
    // flash sans fondu, constaté par Rudy en responsive.
    let maskReady = false;
    let depositsReady = false;
    function maybeStartFade() {
      if (!maskReady || !depositsReady) return;
      const readyTs = performance.now();
      pointsFadeStartTs = readyTs;
      sphereFadeStartTs = readyTs + SPHERE_FADE_DELAY_MS;
    }
    // Distingue "le fondu n'a pas encore commencé" de "le fondu est terminé"
    // -- les deux se traduisent par pointsFadeStartTs === null. Sans ce
    // distinguo, un matériau créé APRÈS la fin du fondu (nouveau pseudo
    // rencontré tardivement, cf. makePointMaterial/pointMaterialForPseudo
    // plus bas -- réseau mobile plus lent que le traitement du masque
    // terre/océan, qui déclenche le fondu) démarrait quand même à
    // opacity:0 et restait invisible pour toujours, faute de boucle de
    // fondu encore active pour le faire remonter à 1 -- point manquant sur
    // son tracé, constaté par Rudy en responsive.
    let pointsFadeDone = false;
    alphabetSphere.setFade(0);

    fetch('/api/sphere-settings')
      .then((res) => (res.ok ? res.json() : null))
      .then((settings) => { if (settings) alphabetSphere.applySettings(settings); })
      .catch(() => {}); // reste sur DEFAULT_SPHERE_SETTINGS si l'API ne répond pas

    // Recalcule les poids (nombre de punchlines par écriture) depuis les
    // vraies données -- pas de plafond, la formule de saturation du shader
    // gère seule n'importe quel volume (voir alphabetSphere.js). Appelée au
    // chargement, puis à chaque ajout/suppression/modif de punchline (cf.
    // liveEvents plus bas) : la sphère reflète toujours l'état réel, sans
    // recharger la page.
    async function refreshAlphabetWeights() {
      try {
        const generations = await fetchGenerations();
        alphabetSphere.setWeights(computeWeightsFromGenerations(generations));
      } catch (error) {
        console.error('[sillage] Échec du calcul des poids de la sphère des alphabets', error);
      }
    }
    refreshAlphabetWeights();

    sampleLandOceanMask('/assets/images/sillage/earth_specular.jpg').then((mask) => {
      const cleaned = cleanLandOceanMask(mask);
      const earthTexture = buildCleanedMaskTexture(cleaned);
      globeMaterial.uniforms.uEarthTexture.value = earthTexture;
      coastlineHaloMaterial.uniforms.uEarthTexture.value = earthTexture;
      landFillMaterial.uniforms.uEarthTexture.value = earthTexture;
      globeMesh.geometry.dispose();
      globeMesh.geometry = buildLandOnlyWireframe(globeGeometry, cleaned, SPHERE_RADIUS * 0.95);
      coastlineMesh.geometry.dispose();
      coastlineMesh.geometry = buildCoastlineContour(cleaned, SPHERE_RADIUS * 0.95);
      // Doublon exact du même tracé pour le halo (pas de grille séparée) :
      // garantit qu'il suit précisément le littoral, sans décalage.
      coastlineHaloMesh.geometry.dispose();
      coastlineHaloMesh.geometry = buildFatLineSegmentsGeometry(coastlineMesh.geometry);
      maskReady = true;
      maybeStartFade();
    });

    // Les deux boutons de révélation (globe plein / demi-globe "près des
    // publications") sont mutuellement exclusifs : activer l'un désactive
    // l'autre (demande Rudy). Au chargement, ni l'un ni l'autre -- le globe
    // reste invisible (cf. commentaire près de sphereFadeStartTs plus haut).
    globeBtn.addEventListener('click', () => {
      const revealAll = !revealUniforms.uRevealAll.value;
      revealUniforms.uRevealAll.value = revealAll;
      if (revealAll) revealUniforms.uProximityReveal.value = false;
      globeBtn.classList.toggle('is-active', revealAll);
      proximityBtn.classList.toggle('is-active', false);
    });
    proximityBtn.addEventListener('click', () => {
      const proximityReveal = !revealUniforms.uProximityReveal.value;
      revealUniforms.uProximityReveal.value = proximityReveal;
      if (proximityReveal) revealUniforms.uRevealAll.value = false;
      proximityBtn.classList.toggle('is-active', proximityReveal);
      globeBtn.classList.toggle('is-active', false);
    });

    // Sous-échantillonne (répartition régulière, pas une troncature) les
    // directions fournies jusqu'au plafond donné -- garde le budget de
    // cuisson de updateRevealTexture prévisible même avec beaucoup de
    // dépôts/points de courbe.
    function subsampleDirections(directions, maxCount) {
      if (directions.length <= maxCount) return directions;
      const stride = directions.length / maxCount;
      const out = [];
      for (let i = 0; i < maxCount; i++) out.push(directions[Math.floor(i * stride)]);
      return out;
    }

    function smoothstepJS(edge0, edge1, x) {
      const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
      return t * t * (3 - 2 * t);
    }

    // Cuit le masque de révélation (points + traits) dans revealTexture --
    // voir le commentaire près de sa création pour le pourquoi (remplace un
    // recalcul par pixel à chaque frame). Reproduit exactement la même
    // formule que l'ancien revealAlpha()/revealMask() GLSL, une fois par
    // texel plutôt qu'une fois par pixel affiché à chaque frame.
    const revealTexDirTmp = new THREE.Vector3();
    function updateRevealTexture(pointDirections, lineDirections) {
      const points = subsampleDirections(pointDirections, GLOBE_REVEAL_BAKE_MAX_POINTS);
      const lines = subsampleDirections(lineDirections, GLOBE_REVEAL_BAKE_MAX_LINE_POINTS);
      const data = revealTexImageData.data;
      let idx = 0;
      for (let j = 0; j < REVEAL_TEX_H; j++) {
        const v = (j + 0.5) / REVEAL_TEX_H;
        const phi = v * Math.PI;
        for (let i = 0; i < REVEAL_TEX_W; i++) {
          const u = (i + 0.5) / REVEAL_TEX_W;
          const theta = u * Math.PI * 2;
          const dir = revealTexDirTmp.set(
            -Math.sin(phi) * Math.cos(theta),
            Math.cos(phi),
            Math.sin(phi) * Math.sin(theta),
          );

          let minPointAngle = Math.PI;
          for (let k = 0; k < points.length; k++) {
            const d = Math.acos(THREE.MathUtils.clamp(dir.dot(points[k]), -1, 1));
            if (d < minPointAngle) minPointAngle = d;
          }
          let minLineAngle = Math.PI;
          for (let k = 0; k < lines.length; k++) {
            const d = Math.acos(THREE.MathUtils.clamp(dir.dot(lines[k]), -1, 1));
            if (d < minLineAngle) minLineAngle = d;
          }

          const pointFade = 1 - smoothstepJS(GLOBE_REVEAL_ANGLE_RAD * 0.55, GLOBE_REVEAL_ANGLE_RAD, minPointAngle);
          const lineFade = 1 - smoothstepJS(GLOBE_REVEAL_LINE_ANGLE_RAD * 0.55, GLOBE_REVEAL_LINE_ANGLE_RAD, minLineAngle);
          const pointFrac = minPointAngle <= GLOBE_REVEAL_ANGLE_RAD ? 0.35 + 0.65 * pointFade : 0;
          const lineFrac = minLineAngle <= GLOBE_REVEAL_LINE_ANGLE_RAD ? 0.35 + 0.65 * lineFade : 0;

          data[idx] = Math.round(pointFrac * 255);
          data[idx + 1] = Math.round(lineFrac * 255);
          data[idx + 2] = 0;
          data[idx + 3] = 255;
          idx += 4;
        }
      }
      revealTexCtx.putImageData(revealTexImageData, 0, 0);
      revealTexture.needsUpdate = true;
    }

    // Points/traits colorés (dépôts) : matériaux suivis ici pour leur fondu
    // d'apparition (voir pointsFadeStartTs plus haut et sa mise à jour dans
    // animate()) -- transparent+depthWrite:true pour que
    // l'opacité s'anime sans casser le tri en profondeur des points une fois
    // à pleine opacité (sinon, un THREE.MeshStandardMaterial transparent
    // classique passe aussi depthWrite à false, faisant apparaître les points
    // superposés dans le mauvais ordre en permanence, pas seulement pendant
    // le fondu).
    const pointFadeMaterials = [];
    function makePointMaterial(params) {
      // Si le fondu global est déjà terminé, ce matériau arrive après coup
      // (nouveau pseudo) -- il doit être visible tout de suite, pas
      // repartir invisible sans plus jamais être ranimé (cf. pointsFadeDone).
      const opacity = pointsFadeDone ? 1 : 0;
      const material = new THREE.MeshStandardMaterial({ ...params, transparent: true, depthWrite: true, opacity });
      if (!pointsFadeDone) pointFadeMaterials.push(material);
      return material;
    }
    const pointGeometry = new THREE.SphereGeometry(POINT_RADIUS, 16, 12);
    const pointMaterial = makePointMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.1 });
    const pointMaterialByPseudo = new Map();
    function pointMaterialForPseudo(pseudo) {
      if (!pseudo) return pointMaterial;
      if (!pointMaterialByPseudo.has(pseudo)) {
        pointMaterialByPseudo.set(pseudo, makePointMaterial({
          color: colorForPseudo(pseudo), roughness: 0.4, metalness: 0.1,
        }));
      }
      return pointMaterialByPseudo.get(pseudo);
    }

    // Point mêlant plusieurs contributeurs (regroupement par proximité) : au
    // lieu d'une couleur unique trompeuse, un dégradé bande par bande (nord ->
    // sud, dans le repère local du point) entre les couleurs des contributeurs
    // présents — via des couleurs par sommet sur une géométrie clonée.
    const gradientPointMaterial = makePointMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.1, vertexColors: true });
    const pointGradientCache = new Map();
    function buildGradientPointGeometry(colors) {
      const geometry = pointGeometry.clone();
      const posAttr = geometry.attributes.position;
      const colorArray = new Float32Array(posAttr.count * 3);
      const tmp = new THREE.Color();
      for (let i = 0; i < posAttr.count; i++) {
        const t = THREE.MathUtils.clamp((posAttr.getY(i) + POINT_RADIUS) / (2 * POINT_RADIUS), 0, 1);
        const scaled = t * (colors.length - 1);
        const idx = Math.floor(scaled);
        tmp.copy(colors[idx]).lerp(colors[Math.min(idx + 1, colors.length - 1)], scaled - idx);
        colorArray[i * 3] = tmp.r;
        colorArray[i * 3 + 1] = tmp.g;
        colorArray[i * 3 + 2] = tmp.b;
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colorArray, 3));
      return geometry;
    }
    function pointVisualForPseudos(pseudos) {
      if (pseudos.length === 0) return { geometry: pointGeometry, material: pointMaterial };
      if (pseudos.length === 1) return { geometry: pointGeometry, material: pointMaterialForPseudo(pseudos[0]) };
      const key = pseudos.slice().sort().join('|');
      if (!pointGradientCache.has(key)) {
        pointGradientCache.set(key, buildGradientPointGeometry(pseudos.map(colorForPseudo)));
      }
      return { geometry: pointGradientCache.get(key), material: gradientPointMaterial };
    }

    // WebGL ignore le réglage natif "linewidth" des THREE.Line sur la quasi-
    // totalité des navigateurs/GPU (limitation ANGLE, pas un bug du projet) :
    // pour une vraie épaisseur en pixels, chaque segment est reconstruit comme
    // un petit ruban de 2 triangles, étendu perpendiculairement à l'écran dans
    // le vertex shader — une "fat line" maison, sans dépendance supplémentaire.
    // La couleur est portée par sommet (pas un uniform) : sur un segment qui
    // rejoint un point à plusieurs contributeurs, le GPU interpole nativement
    // entre la couleur du marcheur et celle du point, donnant un dégradé.
    const LINE_WIDTH_PX = 0.5;
    const fatLineMaterial = new THREE.ShaderMaterial({
      uniforms: {
        resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        lineWidth: { value: LINE_WIDTH_PX },
        // Fondu d'apparition (voir pointsFadeStartTs) : à 0 par défaut,
        // animé dans animate().
        lineOpacity: { value: 0 },
        fogNear: { value: 4 },
        fogFar: { value: 16 },
        fogColor: { value: new THREE.Color(0x000000) },
      },
      vertexShader: `
        uniform vec2 resolution;
        uniform float lineWidth;
        attribute vec3 segStart;
        attribute vec3 segEnd;
        attribute float side;
        attribute vec3 vertexColor;
        varying float vFogDepth;
        varying vec3 vColor;
        void main() {
          // segStart/segEnd sont identiques pour les 4 sommets d'un même segment
          // (contrairement à "position", qui vaut tantôt segStart tantôt segEnd) :
          // la direction/normale calculée est donc la même pour tout le monde —
          // sinon les deux extrémités du ruban se tordent en triangle au lieu de
          // rester parallèles.
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vec4 clip = projectionMatrix * mvPosition;

          vec4 clipStart = projectionMatrix * modelViewMatrix * vec4(segStart, 1.0);
          vec4 clipEnd = projectionMatrix * modelViewMatrix * vec4(segEnd, 1.0);
          vec2 screenStart = (clipStart.xy / clipStart.w) * resolution * 0.5;
          vec2 screenEnd = (clipEnd.xy / clipEnd.w) * resolution * 0.5;
          vec2 dir = (screenStart == screenEnd) ? vec2(1.0, 0.0) : normalize(screenEnd - screenStart);
          vec2 normal = vec2(-dir.y, dir.x);
          vec2 pixelOffset = normal * side * (lineWidth * 0.5);
          vec2 ndcOffset = pixelOffset / (resolution * 0.5);

          gl_Position = clip;
          gl_Position.xy += ndcOffset * clip.w;
          vFogDepth = -mvPosition.z;
          vColor = vertexColor;
        }
      `,
      fragmentShader: `
        uniform float lineOpacity;
        uniform float fogNear;
        uniform float fogFar;
        uniform vec3 fogColor;
        varying float vFogDepth;
        varying vec3 vColor;
        void main() {
          float fogFactor = clamp((vFogDepth - fogNear) / (fogFar - fogNear), 0.0, 1.0);
          gl_FragColor = vec4(mix(vColor, fogColor, fogFactor), lineOpacity);
        }
      `,
      transparent: true,
      depthWrite: false,
    });

    function buildFatLine(points, colors) {
      const positions = [];
      const segStarts = [];
      const segEnds = [];
      const sides = [];
      const vertexColors = [];
      const indices = [];
      let vertIndex = 0;
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        const ca = colors[i];
        const cb = colors[i + 1];
        positions.push(a.x, a.y, a.z,  a.x, a.y, a.z,  b.x, b.y, b.z,  b.x, b.y, b.z);
        segStarts.push(a.x, a.y, a.z,  a.x, a.y, a.z,  a.x, a.y, a.z,  a.x, a.y, a.z);
        segEnds.push(b.x, b.y, b.z,  b.x, b.y, b.z,  b.x, b.y, b.z,  b.x, b.y, b.z);
        sides.push(-1, 1, -1, 1);
        vertexColors.push(ca.r, ca.g, ca.b,  ca.r, ca.g, ca.b,  cb.r, cb.g, cb.b,  cb.r, cb.g, cb.b);
        indices.push(vertIndex, vertIndex + 2, vertIndex + 1,  vertIndex + 2, vertIndex + 3, vertIndex + 1);
        vertIndex += 4;
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('segStart', new THREE.Float32BufferAttribute(segStarts, 3));
      geometry.setAttribute('segEnd', new THREE.Float32BufferAttribute(segEnds, 3));
      geometry.setAttribute('side', new THREE.Float32BufferAttribute(sides, 1));
      geometry.setAttribute('vertexColor', new THREE.Float32BufferAttribute(vertexColors, 3));
      geometry.setIndex(indices);
      return new THREE.Mesh(geometry, fatLineMaterial);
    }

    // Lumière d'appoint faible (les points ne sont jamais totalement noirs côté
    // ombre) + lumière ponctuelle attachée à la caméra (toujours du point de vue
    // du spectateur) pour donner un vrai modelé/volume aux points plutôt qu'un
    // aplat blanc uniforme.
    scene.add(new THREE.AmbientLight(0xffffff, 0.45));
    const keyLight = new THREE.PointLight(0xffffff, 1.2, 0, 0);
    scene.add(camera);
    camera.add(keyLight);

    // Caméra fixe qui regarde toujours le centre du globe (0,0,0) ; la rotation
    // manuelle pivote librement le nuage de points autour de ce même centre.
    // Orientation (lacet) et inclinaison (tangage) sont deux angles absolus,
    // recalculés à chaque changement plutôt que composés par petites touches,
    // pour rester synchronisés avec les curseurs du panneau boussole — sans
    // aucune limite (le globe peut tourner entièrement sur lui-même).
    let radius = 9;
    // RADIUS_MAX doit rester assez haut pour que computeMinRadiusForSideMargin
    // (plus bas) puisse toujours reculer la caméra suffisamment sur un écran
    // étroit et haut (mobile portrait/PWA) -- sur un iPhone standard en
    // portrait, la marge de SAFE_ZONE_SIDE_MARGIN_PX (50px de chaque côté)
    // exige déjà un rayon proche de 25-30 ; sur un écran encore plus étroit
    // ça peut monter à ~40. Un plafond à 20 (ancienne valeur) coupait donc
    // ce calcul court sur mobile : le globe/la sphère/les points touchaient
    // les bords malgré la marge demandée, constaté par Rudy en
    // responsive/PWA. Loin sous le plan de clipping (far=100, cf. new
    // THREE.PerspectiveCamera plus haut), donc marge de sécurité conservée.
    const RADIUS_MIN = 0.01, RADIUS_MAX = 50;
    // Limite de dézoom manuel (molette/pincement/respiration idle) --
    // distincte de RADIUS_MAX : celui-ci doit rester large pour que
    // computeMinRadiusForSideMargin puisse reculer la caméra autant que
    // nécessaire sur un écran étroit (cf. plus haut), mais ça veut dire
    // qu'en portrait/PWA cette limite technique dépasse largement le
    // cadrage "Tout voir" -- sans plafond séparé, l'utilisateur pouvait
    // dézoomer bien au-delà de ce que la page affiche déjà à l'ouverture,
    // avec un globe minuscule perdu au milieu de l'écran (constaté par
    // Rudy en responsive/PWA). Mise à jour avec le cadrage courant à
    // chaque fois qu'il est (re)calculé (chargement, redimensionnement).
    let maxZoomOutRadius = RADIUS_MAX;
    const ROTATE_SPEED = 0.0025;
    const ROTATE_SPEED_REFERENCE_SURFACE_DISTANCE = 9 - SPHERE_RADIUS;
    const ROTATE_SPEED_MIN_FACTOR = 0.016;
    // Un même déplacement de souris fait tourner le globe du même angle
    // quel que soit le zoom, mais une fois la caméra tout près de la
    // surface, ce même angle déplace les points sur une bien plus grande
    // portion de l'écran (le FOV ne couvre plus qu'un petit bout de
    // sphère) — d'où une navigation très nerveuse de près. On réduit donc
    // la sensibilité selon la distance à la surface plutôt que le rayon
    // brut, pour rester maniable au plus près (repérage des points).
    // Le facteur n'est PLUS plafonné à 1 au-delà de la distance de
    // référence : le plafonner revenait à figer la vitesse pour tout le
    // globe dézoomé (le rayon peut monter jusqu'à RADIUS_MAX, bien au-delà
    // de cette référence), qui finissait donc toujours par sembler trop
    // lent — et remonter ROTATE_SPEED pour compenser ne faisait que
    // pousser le niveau intermédiaire (non plafonné, lui) au même rythme,
    // sans jamais changer leur vitesse relative.
    function currentRotateSpeed() {
      const surfaceDistance = Math.max(0.01, radius - SPHERE_RADIUS);
      const factor = Math.max(
        ROTATE_SPEED_MIN_FACTOR,
        surfaceDistance / ROTATE_SPEED_REFERENCE_SURFACE_DISTANCE,
      );
      return ROTATE_SPEED * factor;
    }
    const AXIS_Y = new THREE.Vector3(0, 1, 0);
    const AXIS_X = new THREE.Vector3(1, 0, 0);
    const homeQuaternion = new THREE.Quaternion();
    let orientationRad = 0;
    let tiltRad = 0;

    function updateCamera() {
      camera.position.set(0, 0, radius);
      camera.lookAt(0, 0, 0);
    }
    updateCamera();

    function updateOrientation() {
      const qYaw = new THREE.Quaternion().setFromAxisAngle(AXIS_Y, orientationRad);
      const qTilt = new THREE.Quaternion().setFromAxisAngle(AXIS_X, tiltRad);
      pivotGroup.quaternion.copy(homeQuaternion).premultiply(qYaw).premultiply(qTilt);
    }

    function applyDragRotation(dx, dy) {
      const speed = currentRotateSpeed();
      orientationRad += dx * speed;
      tiltRad += dy * speed;
      updateOrientation();
    }

    function resetOrientation() {
      orientationRad = 0;
      tiltRad = 0;
      updateOrientation();
    }

    // ── Rotation/zoom automatique en veille (plein écran, inactivité) ─────
    // Comme la page AR desktop : après IDLE_TIMEOUT_MS sans interaction en
    // plein écran, le globe tourne lentement tout seul sur ses deux axes
    // (orientation + inclinaison) pendant que le zoom oscille doucement
    // ("par vague") autour du niveau où l'utilisateur l'avait laissé.
    // La moindre interaction interrompt la veille et rend la main.
    const IDLE_TIMEOUT_MS = 6000;
    const AUTO_YAW_SPEED = 0.045;
    const AUTO_TILT_SPEED = 0.02;
    const AUTO_ZOOM_AMPLITUDE_RATIO = 0.18;
    const AUTO_ZOOM_PERIOD_S = 34;

    let lastInteractionTime = performance.now();
    let autoIdleActive = false;
    let autoBaseRadius = radius;
    let autoZoomPhase = 0;
    let autoLastTs = null;

    function markInteraction() {
      lastInteractionTime = performance.now();
      autoIdleActive = false;
    }
    onEnterFullscreen = markInteraction;

    function updateAutoIdle(ts) {
      if (!sillageFullscreen || isDragging || touchMode) {
        autoIdleActive = false;
        autoLastTs = null;
        return;
      }
      if (!autoIdleActive) {
        if (ts - lastInteractionTime < IDLE_TIMEOUT_MS) return;
        autoIdleActive = true;
        autoBaseRadius = radius;
        autoZoomPhase = 0;
        autoLastTs = ts;
        return;
      }
      const dt = autoLastTs ? Math.min((ts - autoLastTs) / 1000, 0.1) : 0;
      autoLastTs = ts;

      orientationRad += AUTO_YAW_SPEED * dt;
      tiltRad += AUTO_TILT_SPEED * dt;
      updateOrientation();

      autoZoomPhase += dt;
      const wave = Math.sin((autoZoomPhase / AUTO_ZOOM_PERIOD_S) * Math.PI * 2);
      radius = THREE.MathUtils.clamp(autoBaseRadius * (1 + wave * AUTO_ZOOM_AMPLITUDE_RATIO), RADIUS_MIN, maxZoomOutRadius);
      updateCamera();
    }

    // ── Barre d'aide ─────────────────────────────────────────────────────
    const hintBar = document.getElementById('sillage-hint-bar');
    const hintClose = document.getElementById('sillage-hint-close');
    hintClose.addEventListener('click', () => {
      hintBar.hidden = true;
    });

    function onResize() {
      // L'export vidéo impose sa propre taille au canvas et rappelle
      // onResize() une fois terminé.
      if (exportInProgress) return;
      camera.aspect = window.innerWidth / window.innerHeight;
      applyViewOffset();
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      fatLineMaterial.uniforms.resolution.value.set(window.innerWidth, window.innerHeight);
      coastlineHaloMaterial.uniforms.resolution.value.set(window.innerWidth, window.innerHeight);
      // Réapplique la marge latérale minimale (cf. computeMinRadiusForSideMargin) :
      // n'agrandit le rayon (donc ne rapetisse le globe) que si nécessaire,
      // jamais l'inverse -- un zoom avant déjà choisi par l'utilisateur
      // n'est jamais annulé par un redimensionnement.
      const minRadius = computeMinRadiusForSideMargin();
      if (radius < minRadius) {
        radius = Math.min(RADIUS_MAX, minRadius);
        updateCamera();
      }
      maxZoomOutRadius = Math.max(maxZoomOutRadius, Math.min(RADIUS_MAX, minRadius));
      if (!viewfinderEl.hidden) positionViewfinder();
    }
    window.addEventListener('resize', onResize);

    // ── Rotation / zoom souris ────────────────────────────────────────
    let isDragging = false;
    let dragMoved = false;
    let prevX = 0, prevY = 0;
    let downX = 0, downY = 0;

    // Le tactile est entièrement géré plus bas (touchstart/move/end) : sur
    // un écran tactile, chaque doigt déclenche AUSSI des événements pointer
    // (spec Pointer Events, pointerType 'touch') en plus des événements
    // touch — sans ce filtre, la logique "souris" ci-dessous tournait en
    // parallèle de celle du pincement pendant un geste à deux doigts,
    // chacune appliquant sa propre rotation/zoom de façon incohérente
    // (rotation parasite très rapide, zoom qui répond mal).
    canvas.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      isDragging = true;
      dragMoved = false;
      prevX = downX = e.clientX;
      prevY = downY = e.clientY;
      markInteraction();
    });
    window.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (!isDragging) return;
      if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) dragMoved = true;
      applyDragRotation(e.clientX - prevX, e.clientY - prevY);
      prevX = e.clientX; prevY = e.clientY;
    });
    window.addEventListener('pointerup', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (isDragging && !dragMoved) handlePick(e.clientX, e.clientY);
      isDragging = false;
    });

    function onWheelZoom(e) {
      e.preventDefault();
      // Zoom multiplicatif (proportionnel au rayon actuel) plutôt
      // qu'un pas fixe : un pas fixe devient énorme relativement une fois
      // très zoomé, rendant le zoom quasi incontrôlable de près.
      const zoomFactor = Math.exp(e.deltaY * 0.0003);
      radius = THREE.MathUtils.clamp(radius * zoomFactor, RADIUS_MIN, maxZoomOutRadius);
      markInteraction();
      updateCamera();
    }
    canvas.addEventListener('wheel', onWheelZoom, { passive: false });

    // ── Tactile : un doigt tourne, deux doigts pincent pour zoomer ─────
    let touchMode = null; // 'rotate' | 'pinch'
    let pinchStartDist = 0;
    let pinchStartRadius = radius;
    let touchStartX = 0, touchStartY = 0, touchMoved = false, touchStartTime = 0;

    function touchDist(t0, t1) {
      return Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
    }

    // Amortissement du zoom par pincement : suffisamment fort pour rester
    // maniable de près (ajuster entre des points voisins), tout en zoomant
    // significativement à chaque geste — sinon atteindre un cluster depuis
    // loin demande un nombre de pincements à rallonge.
    const PINCH_ZOOM_DAMPING = 0.35;
    // Rotation à un doigt encore ralentie par rapport à la souris (même
    // ROTATE_SPEED de base) : un déplacement du doigt reste plus nerveux.
    const TOUCH_ROTATE_DAMPING = 0.55;
    // La caméra ne fait que reculer/avancer pendant un pincement (le rayon
    // change, l'orientation non) : la zone au centre de l'écran reste donc
    // déjà fixe par construction. Le "décalage" ressenti vient plutôt des
    // transitions entre pincements successifs — un doigt qui se repose une
    // fraction de seconde avant l'autre peut être lu comme un bref geste à
    // un seul doigt et déclencher une petite rotation parasite. On ignore
    // donc tout geste à un doigt qui démarre juste après un pincement.
    const PINCH_ROTATE_GRACE_MS = 250;
    let lastPinchEndTime = 0;

    function pinchRadius(startRadius, startDist, t0, t1) {
      const rawScale = startDist / Math.max(1, touchDist(t0, t1));
      const scale = 1 + (rawScale - 1) * PINCH_ZOOM_DAMPING;
      return Math.max(RADIUS_MIN, Math.min(maxZoomOutRadius, startRadius * scale));
    }

    canvas.addEventListener('touchstart', (e) => {
      markInteraction();
      if (e.touches.length === 1) {
        touchMode = (performance.now() - lastPinchEndTime < PINCH_ROTATE_GRACE_MS) ? null : 'rotate';
        prevX = touchStartX = e.touches[0].clientX;
        prevY = touchStartY = e.touches[0].clientY;
        touchMoved = false;
        touchStartTime = performance.now();
      } else if (e.touches.length === 2) {
        touchMode = 'pinch';
        pinchStartDist = touchDist(e.touches[0], e.touches[1]);
        pinchStartRadius = radius;
      }
    }, { passive: true });

    canvas.addEventListener('touchmove', (e) => {
      // Empêche explicitement tout zoom/pan natif du navigateur pendant un
      // pincement à deux doigts (au-delà de touch-action: none, qui ne
      // suffit pas toujours à elle seule sur Safari iOS) — sinon le zoom
      // natif de la page peut s'ajouter à celui du globe et faire dériver
      // la zone visée.
      if (e.touches.length === 2) e.preventDefault();
      if (touchMode === 'rotate' && e.touches.length === 1) {
        const t = e.touches[0];
        if (Math.abs(t.clientX - touchStartX) > 6 || Math.abs(t.clientY - touchStartY) > 6) touchMoved = true;
        // Amortissement propre au doigt (au-delà de ROTATE_SPEED, partagé
        // avec la souris) : au toucher, un même déplacement en pixels reste
        // trop nerveux par rapport à un glissement souris.
        applyDragRotation((t.clientX - prevX) * TOUCH_ROTATE_DAMPING, (t.clientY - prevY) * TOUCH_ROTATE_DAMPING);
        prevX = t.clientX; prevY = t.clientY;
      } else if (touchMode === 'pinch' && e.touches.length === 2) {
        radius = pinchRadius(pinchStartRadius, pinchStartDist, e.touches[0], e.touches[1]);
        updateCamera();
      }
    }, { passive: false });

    canvas.addEventListener('touchend', (e) => {
      if (touchMode === 'rotate' && !touchMoved && performance.now() - touchStartTime < 400) {
        handlePick(touchStartX, touchStartY);
      }
      if (touchMode === 'pinch') lastPinchEndTime = performance.now();
      if (e.touches.length === 0) touchMode = null;
    });

    // Fenêtre phrase/photo ouverte : son fond couvre la sphère. La molette
    // et le pincement y zooment quand même (choisir un point de près, puis
    // reculer pour jouer sur le rapport fenêtre/sphère) ; pas de rotation,
    // et un clic ou un tap à côté referme toujours la fenêtre.
    panelBackdrop.addEventListener('wheel', onWheelZoom, { passive: false });
    let panelPinchDist = 0;
    let panelPinchRadius = radius;
    panelBackdrop.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 2) return;
      panelPinchDist = touchDist(e.touches[0], e.touches[1]);
      panelPinchRadius = radius;
      markInteraction();
    }, { passive: true });
    panelBackdrop.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 2 || !panelPinchDist) return;
      e.preventDefault();
      radius = pinchRadius(panelPinchRadius, panelPinchDist, e.touches[0], e.touches[1]);
      updateCamera();
    }, { passive: false });
    panelBackdrop.addEventListener('touchend', (e) => {
      if (!panelPinchDist || e.touches.length >= 2) return;
      panelPinchDist = 0;
      panelPinchEndedAt = performance.now();
    });

    // ── Survol / clic sur un point ──────────────────────────────────────
    // Test en espace écran (distance au centre projeté de chaque point)
    // plutôt qu'une intersection 3D précise du rayon avec la géométrie : ça
    // donne une zone cliquable généreuse et constante en pixels, quel que
    // soit le niveau de zoom — un raycaster classique reste très précis même
    // très zoomé, ce qui rend les petits points quasi impossibles à viser.
    const PICK_RADIUS_PX = 28;
    let hoveredMesh = null;
    const pickScreenPos = new THREE.Vector3();

    function pickMeshAt(clientX, clientY) {
      let closest = null;
      let closestDistSq = Infinity;
      for (const mesh of pointMeshes) {
        mesh.getWorldPosition(pickScreenPos);
        pickScreenPos.project(camera);
        if (pickScreenPos.z > 1) continue; // derrière la caméra
        const sx = (pickScreenPos.x * 0.5 + 0.5) * window.innerWidth;
        const sy = (-pickScreenPos.y * 0.5 + 0.5) * window.innerHeight;
        const dx = sx - clientX;
        const dy = sy - clientY;
        const distSq = dx * dx + dy * dy;
        if (distSq < closestDistSq) { closestDistSq = distSq; closest = mesh; }
      }
      return closestDistSq <= PICK_RADIUS_PX * PICK_RADIUS_PX ? closest : null;
    }

    function handlePick(clientX, clientY) {
      const mesh = pickMeshAt(clientX, clientY);
      if (!mesh?.userData?.deposit) return;
      const group = mesh.userData.group || [mesh.userData.deposit];
      const startIndex = Math.max(0, group.indexOf(mesh.userData.deposit));
      openPanel(group, startIndex);
    }

    canvas.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (isDragging) return;
      const mesh = pickMeshAt(e.clientX, e.clientY);
      if (mesh !== hoveredMesh) {
        hoveredMesh = mesh;
        canvas.classList.toggle('is-hover', Boolean(hoveredMesh));
      }
    });

    // ── Construction de la scène à partir des dépôts ────────────────────
    const pointMeshes = [];
    const lineMeshes = [];
    // Tous les points ont la même taille de base — plus de distinction entre
    // points isolés et points proches (le zoom seul sert à les distinguer).
    const POINT_SCALE = 0.55;

    // ── Filtre contributeurs (ouvert à tous) ─────────────────────────────
    // Restauré aussi par le lien "Voir Sillages" d'une photo (cf.
    // applyPhotoView). null = tout le monde (y compris les nouveaux contributeurs arrivés en
    // direct) ; sinon l'ensemble des pseudos affichés, '' = dépôts sans
    // pseudo ("Anonymes", en blanc comme leurs points).
    const ANONYMOUS_KEY = '';
    let contributorFilter = null;
    let lastDeposits = [];
    let contributorsOpen = false;
    const contributorsEl = document.getElementById('sillage-contributors');
    const contributorsAllEl = contributorsEl.querySelector('.sillage-contrib-all');
    const contributorsGridEl = contributorsEl.querySelector('.sillage-contrib-grid');
    // Au-delà, une colonne de plus (4 au maximum, moins si l'écran est
    // étroit, puis la liste défile).
    const CONTRIBUTORS_PER_COLUMN = 12;
    const CONTRIBUTORS_MAX_COLUMNS = 4;
    const CONTRIBUTORS_COLUMN_WIDTH_PX = 180;

    function contributorRow(label, color, selected, onToggle) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'sillage-contrib-row';
      row.classList.toggle('is-selected', selected);
      row.setAttribute('aria-pressed', String(selected));
      row.style.color = color;
      const box = document.createElement('span');
      box.className = 'sillage-contrib-box';
      const name = document.createElement('span');
      name.textContent = label;
      row.append(box, name);
      row.addEventListener('click', onToggle);
      return row;
    }

    // Ordre de première contribution (comme les couleurs et l'onglet
    // Contributeurs du dashboard : la liste raconte l'histoire du projet),
    // "Anonymes" en dernier. Sert à la liste, à la légende et au nom de
    // fichier des photos.
    let contributorRank = new Map();
    function sortContributorKeys(keys) {
      const rank = (key) => contributorRank.get(key) ?? Infinity;
      const named = keys.filter((key) => key !== ANONYMOUS_KEY).sort((a, b) => rank(a) - rank(b));
      return keys.includes(ANONYMOUS_KEY) ? [...named, ANONYMOUS_KEY] : named;
    }

    function renderContributorList(deposits) {
      const ordered = sortContributorKeys([...new Set(deposits.map((deposit) => deposit.pseudo))]);
      const isShown = (key) => !contributorFilter || contributorFilter.has(key);
      const allShown = ordered.every(isShown);

      contributorsAllEl.replaceChildren(contributorRow('Tout sélectionner', '#ffffff', allShown, () => {
        setContributorFilter(allShown ? new Set() : null);
      }));
      contributorsGridEl.replaceChildren(...ordered.map((key) => contributorRow(
        key === ANONYMOUS_KEY ? 'Anonymes' : key,
        key === ANONYMOUS_KEY ? '#ffffff' : `#${colorForPseudo(key).getHexString()}`,
        isShown(key),
        () => {
          const next = new Set(contributorFilter ?? ordered);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          setContributorFilter(ordered.every((k) => next.has(k)) ? null : next);
        },
      )));
      const maxColumns = Math.max(1, Math.min(
        CONTRIBUTORS_MAX_COLUMNS,
        Math.floor((window.innerWidth - 80) / CONTRIBUTORS_COLUMN_WIDTH_PX),
      ));
      const columns = Math.min(maxColumns, Math.max(1, Math.ceil(ordered.length / CONTRIBUTORS_PER_COLUMN)));
      contributorsGridEl.style.setProperty('--rows', String(Math.max(1, Math.ceil(ordered.length / columns))));
    }

    function setContributorFilter(filter) {
      contributorFilter = filter;
      clearScene();
      buildScene(lastDeposits);
    }

    function setContributorsOpen(open) {
      contributorsOpen = open;
      contributorsEl.classList.toggle('is-open', open);
      contributorsEl.setAttribute('aria-hidden', String(!open));
      contributorsBtn.classList.toggle('is-active', open);
      contributorsBtn.setAttribute('aria-expanded', String(open));
    }

    contributorsBtn.addEventListener('click', () => setContributorsOpen(!contributorsOpen));
    // Clic en dehors (comme la légende) : referme la liste.
    document.addEventListener('pointerdown', (e) => {
      if (!contributorsOpen) return;
      if (contributorsEl.contains(e.target) || contributorsBtn.contains(e.target)) return;
      setContributorsOpen(false);
    });

    function averageColors(colors) {
      const result = new THREE.Color(0, 0, 0);
      for (const c of colors) result.add(c);
      return result.multiplyScalar(1 / colors.length);
    }

    function buildScene(allDeposits) {
      lastDeposits = allDeposits;
      // Pré-passage : attribue les couleurs par date de première contribution
      // (tous types de dépôts confondus), pour qu'un nouveau contributeur
      // soit toujours servi en dernier, quel que soit l'ordre des dépôts.
      // Toujours sur TOUS les dépôts, filtre contributeurs ou pas : les
      // couleurs ne bougent jamais.
      const firstContribution = new Map();
      for (const deposit of allDeposits) {
        if (!deposit.pseudo) continue;
        const time = new Date(deposit.createdAt).getTime();
        const known = firstContribution.get(deposit.pseudo);
        if (known === undefined || time < known) firstContribution.set(deposit.pseudo, time);
      }
      const chronological = [...firstContribution]
        .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
        .map(([pseudo]) => pseudo);
      chronological.forEach((pseudo) => colorForPseudo(pseudo));
      contributorRank = new Map(chronological.map((pseudo, index) => [pseudo, index]));
      renderContributorList(allDeposits);

      // Filtre contributeurs : points, tracés ET terre révélée ne
      // montrent que les contributeurs sélectionnés (demande Rudy).
      const deposits = contributorFilter
        ? allDeposits.filter((deposit) => contributorFilter.has(deposit.pseudo))
        : allDeposits;

      const depositToPos = new Map();
      const depositToColor = new Map();
      const pointDirections = [];
      for (const group of buildProximityGroups(deposits)) {
        const pos = latLngToVector3(group.lat, group.lng, SPHERE_RADIUS);
        const pseudos = distinctPseudos(group.items);
        const { geometry, material } = pointVisualForPseudos(pseudos);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(pos);
        mesh.userData.deposit = group.items[0];
        mesh.userData.group = group.items;
        mesh.userData.baseScale = POINT_SCALE;
        // Toujours par-dessus le remplissage noir des continents
        // (landFillMesh, renderOrder 1) -- un dépôt ne doit jamais
        // disparaître sous une zone révélée (demande Rudy).
        mesh.renderOrder = 3;
        pivotGroup.add(mesh);
        pointMeshes.push(mesh);
        pointDirections.push(pos.clone().normalize());

        const groupColor = pseudos.length === 0 ? new THREE.Color(0xffffff)
          : pseudos.length === 1 ? colorForPseudo(pseudos[0])
          : averageColors(pseudos.map(colorForPseudo));
        for (const deposit of group.items) {
          depositToPos.set(deposit, pos);
          depositToColor.set(deposit, groupColor);
        }
      }

      const lineDirections = [];
      for (const group of buildDeambulationGroups(deposits)) {
        const rawPoints = group.items.map((deposit) => depositToPos.get(deposit));
        const rawColors = group.items.map((deposit) => depositToColor.get(deposit));
        const { points, colors } = curveDeambulationPath(rawPoints, rawColors);
        const line = buildFatLine(points, colors);
        // Même raison que pointMeshes ci-dessus : fatLineMaterial a
        // depthWrite:false (technique "fat line", évite le z-fighting entre
        // segments qui se chevauchent), donc le test de profondeur seul ne
        // suffit pas à les garder visibles par-dessus le remplissage noir --
        // l'ordre de dessin explicite, lui, le garantit.
        line.renderOrder = 3;
        pivotGroup.add(line);
        lineMeshes.push(line);
        for (const p of points) lineDirections.push(p.clone().normalize());
      }
      updateRevealTexture(pointDirections, lineDirections);
    }

    function clearScene() {
      for (const mesh of pointMeshes) pivotGroup.remove(mesh);
      pointMeshes.length = 0;
      for (const line of lineMeshes) {
        pivotGroup.remove(line);
        line.geometry.dispose();
      }
      lineMeshes.length = 0;
      hoveredMesh = null;
    }

    // ── Cadrage par défaut ("Tout voir", nord en haut, amas face caméra) ──
    // Montre le globe entier, dimensionné pour remplir exactement la zone
    // sûre (cf. computeSafeZone/applyViewOffset) avec une petite marge
    // interne, indépendamment des dépôts. L'orientation ne fait pivoter le
    // globe qu'en lacet (autour de l'axe nord-sud) pour amener la longitude
    // moyenne des dépôts face à la caméra — jamais d'inclinaison, donc le
    // pôle nord (+Y local) reste en haut de l'écran.
    // Rayon caméra minimal pour garantir SAFE_ZONE_SIDE_MARGIN_PX de marge
    // laterale (globe/sphère/points/traits, tout tourne ensemble dans
    // pivotGroup) -- extrait en fonction pour être réappliqué aussi bien au
    // cadrage par défaut (computeDefaultView) qu'à chaque redimensionnement
    // (onResize, cf. plus bas) : sans ça, un viewport qui change APRÈS le
    // premier calcul (barre d'adresse mobile qui se rétracte, chrome de la
    // PWA qui se stabilise après le tout premier rendu, rotation d'écran)
    // pouvait laisser le globe toucher les bords en responsive/PWA malgré
    // ce calcul au chargement (constaté par Rudy).
    function computeMinRadiusForSideMargin() {
      const safeWidth = Math.max(50, window.innerWidth - SAFE_ZONE_SIDE_MARGIN_PX * 2);
      const hFovHalf = Math.atan(Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2)) * camera.aspect);
      const tanTargetX = Math.tan(hFovHalf) * (safeWidth * 0.98) / window.innerWidth;
      const targetHalfAngleX = Math.atan(tanTargetX);
      return SPHERE_RADIUS / Math.sin(targetHalfAngleX);
    }

    function computeDefaultView(deposits) {
      const { top, bottom } = computeSafeZone();
      const safeHeight = Math.max(50, bottom - top);
      const tanTarget = Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2))
        * (safeHeight * 0.98) / currentFullHeight;
      const targetHalfAngle = Math.atan(tanTarget);
      const radiusForHeight = SPHERE_RADIUS / Math.sin(targetHalfAngle);

      // Même calcul, transposé à l'horizontale (FOV horizontal déduit du FOV
      // vertical et de l'aspect ratio), pour garantir la marge latérale même
      // en portrait étroit — on garde le plus grand des deux rayons (donc le
      // globe le plus petit) pour respecter les deux contraintes à la fois.
      const radiusForWidth = computeMinRadiusForSideMargin();

      const computedRadius = Math.max(radiusForHeight, radiusForWidth);

      let quaternion = new THREE.Quaternion();
      const sum = new THREE.Vector3();
      for (const deposit of deposits) {
        sum.add(latLngToVector3(deposit.lat, deposit.lng, 1));
      }
      sum.y = 0;
      if (sum.lengthSq() > 1e-6) {
        sum.normalize();
        quaternion = new THREE.Quaternion().setFromUnitVectors(sum, new THREE.Vector3(0, 0, 1));
      }

      return {
        quaternion,
        radius: Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, computedRadius)),
      };
    }

    // ── Taille des points fixe en pixels-écran ────────────────────────────
    // Une géométrie 3D classique grossit/rétrécit avec la distance caméra ;
    // on recalcule donc chaque frame, pour chaque point, le facteur d'échelle
    // qui lui donne exactement TARGET_POINT_PX pixels de diamètre à l'écran,
    // quelle que soit sa distance à la caméra — tous les points ont donc la
    // même taille apparente (hors survol/chevauchement, volontairement différents).
    const TARGET_POINT_PX = 10;
    const worldPos = new THREE.Vector3();

    function clampPointScreenSizes() {
      const tanHalfFov = Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2));
      for (const mesh of pointMeshes) {
        mesh.getWorldPosition(worldPos);
        const dist = camera.position.distanceTo(worldPos);
        const targetWorldRadius = (TARGET_POINT_PX / 2) * dist * tanHalfFov / (currentFullHeight / 2);
        const sizeFactor = targetWorldRadius / POINT_RADIUS;
        const hoverFactor = mesh === hoveredMesh ? 1.6 : 1;
        mesh.scale.setScalar(mesh.userData.baseScale * sizeFactor * hoverFactor);
      }
    }

    // ── Boucle de rendu ──────────────────────────────────────────────────
    // Le `try` plus haut ne protège que l'init synchrone : une erreur ici
    // (ex. compilation de shader qui échoue au premier rendu réel, contexte
    // WebGL perdu...) se reproduisait à chaque frame sans jamais planter le
    // script — le rAF suivant était déjà reprogrammé avant l'erreur — donc
    // sans jamais déclencher showSillageError : canvas silencieusement vide,
    // "une fois sur deux" selon l'aléa GPU/driver. On protège donc aussi la
    // boucle et on l'arrête proprement au lieu de la laisser boucler dans le
    // vide.
    let renderFailed = false;
    // Pendant un export vidéo, c'est lui qui dessine chaque image (cf.
    // exportLoopVideo plus bas) : la boucle de la page se met en pause.
    let exportInProgress = false;

    function updateFog() {
      scene.fog.near = Math.max(0.5, radius - SPHERE_RADIUS - 1);
      scene.fog.far = radius + SPHERE_RADIUS + 4;
      fatLineMaterial.uniforms.fogNear.value = scene.fog.near;
      fatLineMaterial.uniforms.fogFar.value = scene.fog.far;
    }

    function animate(ts) {
      if (renderFailed) return;
      requestAnimationFrame(animate);
      if (exportInProgress) return;
      try {
        updateAutoIdle(ts || performance.now());
        updateFog();
        clampPointScreenSizes();
        const nowTs = ts || performance.now();
        const sphereDt = Math.min(0.05, (nowTs - lastAlphabetSphereTs) / 1000);
        lastAlphabetSphereTs = nowTs;
        alphabetSphere.update(sphereDt, nowTs / 1000);
        if (pointsFadeStartTs !== null) {
          const pointsFade = Math.max(0, Math.min(1, (nowTs - pointsFadeStartTs) / POINTS_FADE_DURATION_MS));
          fatLineMaterial.uniforms.lineOpacity.value = pointsFade;
          for (const material of pointFadeMaterials) material.opacity = pointsFade;
          // Fondu terminé : arrête de réécrire ces opacités à chaque frame
          // pour toujours -- pointFadeMaterials grandit avec le nombre de
          // contributeurs distincts (un matériau par pseudo), donc cette
          // boucle représentait un coût récurrent inutile une fois les
          // points/traits pleinement visibles, pas juste pendant le fondu.
          if (pointsFade >= 1) { pointsFadeStartTs = null; pointsFadeDone = true; }
        }
        if (sphereFadeStartTs !== null) {
          const sphereFade = (nowTs - sphereFadeStartTs) / SPHERE_FADE_DURATION_MS;
          alphabetSphere.setFade(sphereFade);
          if (sphereFade >= 1) sphereFadeStartTs = null;
        }
        renderer.render(scene, camera);
      } catch (error) {
        renderFailed = true;
        showSillageError(error);
      }
    }
    requestAnimationFrame(animate);

    // ── Export vidéo en boucle (admin, plein écran) ─────────────────────
    // "On exporte ce qu'on voit" : mode d'affichage (globe plein, terre
    // révélée ou rien), orientation et zoom de la vue au lancement, puis le
    // mouvement de la veille automatique bouclé sur un tour complet (cf.
    // sillageLoop.js). Chaque image est dessinée pour son instant exact
    // puis encodée (frameEncoder.js) : mouvement parfaitement régulier quelle
    // que soit la charge de la machine. Rendu en 2x puis réduit (particules
    // et traits plus fins/lisses qu'un rendu direct à la taille finale).
    const EXPORT_FPS = 60;
    const EXPORT_BITRATE = 24_000_000;
    const EXPORT_SUPERSAMPLE = 2;
    const EXPORT_REFERENCE_PIXEL_RATIO = 2;
    const EXPORT_FIT_RATIO = 0.94;
    // Un peu plus que le globe : rayon des points (10 px à l'écran) compris.
    const EXPORT_FIT_SPHERE_RADIUS = SPHERE_RADIUS * 1.03;
    const EXPORT_FORMATS = {
      landscape: { width: 1920, height: 1080 },
      portrait: { width: 1080, height: 1920 },
    };
    const exportOverlay = document.getElementById('sillage-export-overlay');
    const exportLabel = document.getElementById('sillage-export-label');
    const exportBarFill = document.getElementById('sillage-export-bar-fill');
    const exportCancelBtn = document.getElementById('sillage-export-cancel');
    let exportAbort = null;
    // Posé dès le clic : le chargement de l'encodeur laisse le temps d'un
    // double-clic, qui lançait sinon deux exports.
    let exportRequested = false;

    // Un export lancé pendant le fondu d'apparition le figerait à mi-course.
    function completeLoadingFades() {
      fatLineMaterial.uniforms.lineOpacity.value = 1;
      for (const material of pointFadeMaterials) material.opacity = 1;
      pointsFadeStartTs = null;
      pointsFadeDone = true;
      alphabetSphere.setFade(1);
      sphereFadeStartTs = null;
    }

    function showExportProgress(label, fraction) {
      exportLabel.textContent = label;
      exportBarFill.style.width = `${Math.round(fraction * 100)}%`;
    }

    function downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }

    async function exportLoopVideo(format) {
      if (exportRequested || !maskReady || !depositsReady) return;
      exportRequested = true;
      try {
        await runLoopVideoExport(format);
      } finally {
        exportRequested = false;
      }
    }

    async function runLoopVideoExport(format) {
      const { width, height } = EXPORT_FORMATS[format];
      const sizeLabel = `${width}×${height}`;

      let encoder;
      try {
        // Module ESM séparé (cf. scripts/build.mjs) : ne pas inliner mediabunny dans sillage.js.
        const encoderUrl = document.querySelector('meta[name="sillage-frame-encoder"]').content;
        encoder = await import(encoderUrl);
      } catch (error) {
        console.error('[sillage] Encodeur vidéo introuvable', error);
        alert("Impossible de charger l'encodeur vidéo.");
        return;
      }
      if (!(await encoder.canEncodeFrames({ width, height, bitrate: EXPORT_BITRATE, fps: EXPORT_FPS }))) {
        alert('Ce navigateur ne sait pas encoder la vidéo image par image (WebCodecs) : utiliser Safari ou Chrome à jour.');
        return;
      }

      const loop = buildSillageLoop({
        fps: EXPORT_FPS,
        yawSpeed: AUTO_YAW_SPEED,
        tiltSpeed: AUTO_TILT_SPEED,
        zoomPeriodS: AUTO_ZOOM_PERIOD_S,
        zoomAmplitude: AUTO_ZOOM_AMPLITUDE_RATIO,
        sphereRotSpeed: alphabetSphere.settings.rotSpeed,
        noiseSpeed: alphabetSphere.settings.flow * 3,
      });
      const start = { orientation: orientationRad, tilt: tiltRad, radius, sphere: alphabetSphere.getPose() };
      const livePixelRatio = renderer.getPixelRatio();

      // Cadrage identique à l'écran : le petit côté de la vidéo couvre le
      // même angle que la hauteur visible de la fenêtre (le cadre de vue de
      // la page, currentFullHeight, déborde de l'écran quand le globe est
      // recentré, cf. applyViewOffset). Toutes les tailles en pixels (traits,
      // particules) suivent le même rapport, pour garder les proportions
      // du rendu à l'écran.
      const shortSide = Math.min(width, height);
      const pixelScale = shortSide / window.innerHeight;
      const visibleTanHalfFov = Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2)) * window.innerHeight / currentFullHeight;

      // Recul automatique : au plus zoomé de la respiration, le globe entier
      // (orbe, points et traits des dépôts, posés à SPHERE_RADIUS) doit
      // tenir dans EXPORT_FIT_RATIO du petit côté, quelle que soit
      // l'orientation -- le cadrage choisi est gardé s'il le permet déjà.
      // Sur le test du 17 octobre 2026, cadrage par défaut, les points
      // d'Europe touchaient le bord haut du 1920x1080 au plus zoomé.
      const fitTan = visibleTanHalfFov * EXPORT_FIT_RATIO;
      const minCloseRadius = EXPORT_FIT_SPHERE_RADIUS * Math.sqrt(1 + 1 / (fitTan * fitTan));
      const baseRadius = Math.max(start.radius, minCloseRadius / (1 - AUTO_ZOOM_AMPLITUDE_RATIO));
      const renderWidth = width * EXPORT_SUPERSAMPLE;
      const renderHeight = height * EXPORT_SUPERSAMPLE;
      const renderScale = pixelScale * EXPORT_SUPERSAMPLE;

      const frameCanvas = document.createElement('canvas');
      frameCanvas.width = width;
      frameCanvas.height = height;
      const frameCtx = frameCanvas.getContext('2d');
      frameCtx.imageSmoothingEnabled = true;
      frameCtx.imageSmoothingQuality = 'high';

      exportInProgress = true;
      exportAbort = new AbortController();
      hoveredMesh = null;
      canvas.classList.remove('is-hover');
      completeLoadingFades();
      showExportProgress(`Export vidéo ${sizeLabel}`, 0);
      exportOverlay.hidden = false;

      try {
        renderer.setPixelRatio(1);
        renderer.setSize(renderWidth, renderHeight, false);
        camera.clearViewOffset();
        camera.aspect = width / height;
        camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(visibleTanHalfFov * height / shortSide));
        camera.updateProjectionMatrix();
        fatLineMaterial.uniforms.resolution.value.set(renderWidth, renderHeight);
        fatLineMaterial.uniforms.lineWidth.value = LINE_WIDTH_PX * renderScale;
        coastlineHaloMaterial.uniforms.resolution.value.set(renderWidth, renderHeight);
        coastlineHaloMaterial.uniforms.haloWidth.value = COASTLINE_HALO_WIDTH_PX * renderScale;
        // gl_PointSize est en pixels réels, donc les particules sont 2x plus
        // fines sur un écran Retina. Toujours calé sur le Retina (le rendu de
        // référence, ratio plafonné à 2 sur la page), quel que soit l'écran
        // qui exporte : constaté par Rudy le 17 octobre 2026, un export
        // depuis un écran classique avait des particules ~1,7x plus grosses.
        alphabetSphere.setPointSizeScale(renderScale / EXPORT_REFERENCE_PIXEL_RATIO);

        const startedAt = performance.now();
        const blob = await encoder.encodeFrames({
          canvas: frameCanvas,
          fps: EXPORT_FPS,
          totalFrames: loop.totalFrames,
          bitrate: EXPORT_BITRATE,
          signal: exportAbort.signal,
          drawFrame: (time) => {
            const state = loop.stateAt(time);
            orientationRad = start.orientation + state.yaw;
            tiltRad = start.tilt + state.tilt;
            updateOrientation();
            // Sans le plafond de dézoom de la page (maxZoomOutRadius) : au
            // cadrage par défaut, il aplatissait la moitié de chaque
            // respiration, et le recul automatique le dépasse.
            radius = THREE.MathUtils.clamp(baseRadius * state.zoom, RADIUS_MIN, RADIUS_MAX);
            updateCamera();
            alphabetSphere.setPose({
              rotationY: start.sphere.rotationY + state.sphereRotation,
              noiseOffset: start.sphere.noiseOffset.map((value, i) => value + state.noiseOffset[i]),
            });
            updateFog();
            clampPointScreenSizes();
            renderer.render(scene, camera);
            // Copie immédiate, avant que le navigateur n'efface le tampon WebGL.
            frameCtx.drawImage(canvas, 0, 0, width, height);
          },
          onProgress: (fraction) => {
            const elapsed = (performance.now() - startedAt) / 1000;
            const remaining = fraction > 0.01 ? Math.ceil((elapsed / fraction - elapsed) / 60) : null;
            const eta = remaining === null ? '' : ` — encore ~${remaining} min`;
            showExportProgress(`Export vidéo ${sizeLabel} : ${Math.round(fraction * 100)} %${eta}`, fraction);
          },
        });
        const date = new Date().toISOString().slice(0, 10);
        downloadBlob(blob, `sillage-boucle-${width}x${height}-${date}.mp4`);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('[sillage] Échec de l\'export vidéo', error);
          alert("L'export vidéo a échoué.");
        }
      } finally {
        orientationRad = start.orientation;
        tiltRad = start.tilt;
        updateOrientation();
        radius = start.radius;
        updateCamera();
        alphabetSphere.setPose(start.sphere);
        alphabetSphere.setPointSizeScale(1);
        fatLineMaterial.uniforms.lineWidth.value = LINE_WIDTH_PX;
        coastlineHaloMaterial.uniforms.haloWidth.value = COASTLINE_HALO_WIDTH_PX;
        renderer.setPixelRatio(livePixelRatio);
        camera.fov = CAMERA_FOV;
        exportInProgress = false;
        exportAbort = null;
        onResize();
        lastAlphabetSphereTs = performance.now();
        markInteraction();
        exportOverlay.hidden = true;
      }
    }

    exportLandscapeBtn.addEventListener('click', () => exportLoopVideo('landscape'));
    exportPortraitBtn.addEventListener('click', () => exportLoopVideo('portrait'));
    exportCancelBtn.addEventListener('click', () => exportAbort?.abort());

    // ── Photo 30x30 cm à 300 dpi (admin, plein écran) ────────────────────
    // Le bouton appareil photo ouvre un viseur carré : on cadre, on ouvre
    // éventuellement une phrase/photo, puis "Déclencher" fige l'instant et
    // le redessine en 3543 x 3543 px. Trop grand pour un seul rendu WebGL :
    // dessiné par tuiles (chacune = un morceau du carré, rendu en 2x puis
    // réduit, comme l'export vidéo), assemblées dans un canvas 2D. Tailles
    // en pixels (traits, halo, particules) agrandies dans le même rapport
    // que l'image, pour garder exactement le rendu de l'écran. Le PNG est
    // téléchargé tout de suite, puis envoyé au site (dashboard, Escales).
    const PHOTO_DPI = 300;
    const PHOTO_SIZE_PX = Math.round((30 / 2.54) * PHOTO_DPI);
    const PHOTO_TILE_PX = 1024;
    // Finesse de la sphère dans la photo (cf. setDetail dans
    // alphabetSphere.js) : grille 3x plus serrée, particules 3x plus fines.
    // Choisie par Rudy le 22 octobre 2026 sur tirage à taille réelle
    // (x1 = grain grossier, x2 et x3 comparés).
    const PHOTO_DETAIL = 3;
    const viewfinderShootBtn = document.getElementById('sillage-viewfinder-shoot');
    const viewfinderCloseBtn = document.getElementById('sillage-viewfinder-close');

    function revealMode() {
      if (revealUniforms.uRevealAll.value) return 'all';
      return revealUniforms.uProximityReveal.value ? 'proximity' : 'none';
    }

    function setRevealMode(mode) {
      revealUniforms.uRevealAll.value = mode === 'all';
      revealUniforms.uProximityReveal.value = mode === 'proximity';
      globeBtn.classList.toggle('is-active', mode === 'all');
      proximityBtn.classList.toggle('is-active', mode === 'proximity');
    }

    // Légende de la photo (Escales, dashboard) : l'auteur de la phrase ou
    // de la photo ouverte au-dessus de la sphère s'il en a un (demande de
    // Rudy le 23 octobre 2026, quel que soit le filtre), "Anonyme" s'il
    // n'en a pas ; sinon les contributeurs affichés par le filtre, dans
    // l'ordre de la liste, ou tout le monde.
    function photoCaption(deposit) {
      if (deposit) return deposit.pseudo || 'Anonyme';
      if (!contributorFilter) return 'Tous les punchliners';
      return sortContributorKeys([...contributorFilter])
        .map((key) => (key === ANONYMOUS_KEY ? 'Anonymes' : key))
        .join(', ');
    }

    // Ex. : sillages-camille+yohan_rdy-2026-10-22-21h27m51.png (heure de
    // l'ordinateur ; les secondes évitent deux noms identiques).
    function photoFilename(caption) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const slug = sphereCaptionSlug(caption);
      return `sillages-${slug ? `${slug}-` : ''}${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
        + `-${pad(now.getHours())}h${pad(now.getMinutes())}m${pad(now.getSeconds())}.png`;
    }

    // Dessine le carré du viseur, tuile par tuile, dans ctx (size x size).
    async function renderViewfinderTiles(ctx, frame, size) {
      const renderScale = (size / frame.width) * EXPORT_SUPERSAMPLE;
      const renderTile = PHOTO_TILE_PX * EXPORT_SUPERSAMPLE;
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Carré du viseur en coordonnées normalisées de l'écran (-1..1, y vers le haut).
      const left = (frame.left / w) * 2 - 1;
      const right = (frame.right / w) * 2 - 1;
      const top = 1 - (frame.top / h) * 2;
      const bottom = 1 - (frame.bottom / h) * 2;
      const screenProjection = camera.projectionMatrix.clone();
      const tileMatrix = new THREE.Matrix4();

      renderer.setPixelRatio(1);
      renderer.setSize(renderTile, renderTile, false);
      fatLineMaterial.uniforms.resolution.value.set(renderTile, renderTile);
      fatLineMaterial.uniforms.lineWidth.value = LINE_WIDTH_PX * renderScale;
      coastlineHaloMaterial.uniforms.resolution.value.set(renderTile, renderTile);
      coastlineHaloMaterial.uniforms.haloWidth.value = COASTLINE_HALO_WIDTH_PX * renderScale;
      // Même référence que l'export vidéo (écran Retina), cf. plus haut.
      alphabetSphere.setPointSizeScale(renderScale / EXPORT_REFERENCE_PIXEL_RATIO);
      alphabetSphere.setDetail(PHOTO_DETAIL);

      const perSide = Math.ceil(size / PHOTO_TILE_PX);
      for (let ty = 0; ty < perSide; ty++) {
        for (let tx = 0; tx < perSide; tx++) {
          const x0 = tx * PHOTO_TILE_PX;
          const y0 = ty * PHOTO_TILE_PX;
          // Morceau du carré couvert par la tuile (taille pleine même au
          // bord : le surplus tombe hors du canvas et n'est pas dessiné).
          const xa = left + ((right - left) * x0) / size;
          const xb = left + ((right - left) * (x0 + PHOTO_TILE_PX)) / size;
          const ya = top + ((bottom - top) * y0) / size;
          const yb = top + ((bottom - top) * (y0 + PHOTO_TILE_PX)) / size;
          // Recadre la projection de l'écran sur ce morceau : la vue est
          // exactement celle de l'écran, perspective comprise.
          tileMatrix.set(
            2 / (xb - xa), 0, 0, -(xa + xb) / (xb - xa),
            0, 2 / (ya - yb), 0, -(ya + yb) / (ya - yb),
            0, 0, 1, 0,
            0, 0, 0, 1,
          );
          camera.projectionMatrix.multiplyMatrices(tileMatrix, screenProjection);
          camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
          renderer.render(scene, camera);
          // Copie immédiate, avant que le navigateur n'efface le tampon WebGL.
          ctx.drawImage(canvas, 0, 0, renderTile, renderTile, x0, y0, PHOTO_TILE_PX, PHOTO_TILE_PX);
          const done = ty * perSide + tx + 1;
          showExportProgress(`Photo 30 × 30 cm : rendu ${done} / ${perSide * perSide}`, (done / (perSide * perSide)) * 0.7);
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }
    }

    async function takeSpherePhoto() {
      if (exportRequested || !maskReady || !depositsReady) return;
      exportRequested = true;
      try {
        await runSpherePhoto();
      } finally {
        exportRequested = false;
      }
    }

    async function runSpherePhoto() {
      fitPanelToViewfinder();
      const frame = viewfinderFrame.getBoundingClientRect();
      const size = PHOTO_SIZE_PX;
      const visibleTanHalfFov = Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2)) * window.innerHeight / currentFullHeight;
      // Cadrage enregistré avec la photo (lien "Voir Sillages" d'Escales).
      const view = {
        home: homeQuaternion.toArray(),
        orientation: orientationRad,
        tilt: tiltRad,
        radius,
        frameTan: visibleTanHalfFov * frame.height / window.innerHeight,
        reveal: revealMode(),
        contributors: contributorFilter ? [...contributorFilter] : null,
      };
      const deposit = panelBackdrop.hidden ? null : panelGroup[panelIndex];
      const caption = photoCaption(deposit);
      // Photo affichée dans le panneau : son original HD (admin) plutôt
      // que la version web de l'écran.
      const photoUrl = deposit?.imagePath ? deposit.imagePath.replace(/\?.*$/, '') : null;

      const photoCanvas = document.createElement('canvas');
      photoCanvas.width = size;
      photoCanvas.height = size;
      const ctx = photoCanvas.getContext('2d');
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const livePixelRatio = renderer.getPixelRatio();
      exportInProgress = true;
      hoveredMesh = null;
      canvas.classList.remove('is-hover');
      completeLoadingFades();
      // Instant figé tel qu'à l'écran (taille des points calculée pour lui).
      updateFog();
      clampPointScreenSizes();
      exportCancelBtn.hidden = true;
      showExportProgress('Photo 30 × 30 cm', 0);
      exportOverlay.hidden = false;

      try {
        try {
          await renderViewfinderTiles(ctx, frame, size);
        } finally {
          alphabetSphere.setPointSizeScale(1);
          alphabetSphere.setDetail(1);
          fatLineMaterial.uniforms.lineWidth.value = LINE_WIDTH_PX;
          coastlineHaloMaterial.uniforms.haloWidth.value = COASTLINE_HALO_WIDTH_PX;
          renderer.setPixelRatio(livePixelRatio);
          exportInProgress = false;
          // Remet la taille du canvas, la projection et les résolutions de l'écran.
          onResize();
          lastAlphabetSphereTs = performance.now();
        }

        showExportProgress('Photo 30 × 30 cm : phrase / photo', 0.75);
        // Carré relu ici : onResize ci-dessus a pu recaler le viseur (et la
        // fenêtre avec lui) sur le globe, déplacé depuis par un zoom.
        await drawPanelOverlay(ctx, {
          frame: viewfinderFrame.getBoundingClientRect(), size, backdrop: panelBackdrop, panel: panelEl, photoUrl, panelScale: panelFrameScale,
        });

        showExportProgress('Photo 30 × 30 cm : fichier PNG', 0.8);
        const raw = await new Promise((resolve) => photoCanvas.toBlob(resolve, 'image/png'));
        if (!raw) throw new Error('Encodage PNG impossible');
        const png = new Blob([withPngDpi(new Uint8Array(await raw.arrayBuffer()), PHOTO_DPI)], { type: 'image/png' });
        const filename = photoFilename(caption);
        downloadBlob(png, filename);

        showExportProgress('Photo 30 × 30 cm : envoi sur le site', 0.9);
        try {
          const form = new FormData();
          form.append('view', JSON.stringify(view));
          form.append('pseudo', caption);
          form.append('photo', png, filename);
          await uploadSpherePhoto(form);
        } catch (error) {
          console.error('[sillage] Envoi de la photo', error);
          alert("La photo est téléchargée sur cet ordinateur, mais n'a pas pu être enregistrée sur le site (dashboard, Escales).");
        }
      } catch (error) {
        console.error('[sillage] Échec de la photo', error);
        alert('La photo a échoué.');
      } finally {
        exportOverlay.hidden = true;
        exportCancelBtn.hidden = false;
        markInteraction();
      }
    }

    // Carré centré sur le globe (pas sur l'écran : la page décale le globe
    // pour le centrer entre header et footer, cf. applyViewOffset), aussi
    // grand que possible avec VIEWFINDER_MARGIN_PX de marge, en gardant la
    // place des boutons à sa droite.
    const VIEWFINDER_MARGIN_PX = 25;
    const VIEWFINDER_BAR_GAP_PX = 18;
    const viewfinderBar = document.getElementById('sillage-viewfinder-bar');
    const globeCenterTmp = new THREE.Vector3();

    function positionViewfinder() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.updateMatrixWorld();
      globeCenterTmp.set(0, 0, 0).project(camera);
      const cx = ((globeCenterTmp.x + 1) / 2) * w;
      const cy = ((1 - globeCenterTmp.y) / 2) * h;
      const barRoom = viewfinderBar.offsetWidth + VIEWFINDER_BAR_GAP_PX;
      const half = Math.max(50, Math.min(
        cy - VIEWFINDER_MARGIN_PX,
        h - cy - VIEWFINDER_MARGIN_PX,
        cx - VIEWFINDER_MARGIN_PX,
        w - cx - VIEWFINDER_MARGIN_PX - barRoom,
      ));
      viewfinderEl.style.setProperty('--vf-left', `${Math.round(cx - half)}px`);
      viewfinderEl.style.setProperty('--vf-top', `${Math.round(cy - half)}px`);
      viewfinderEl.style.setProperty('--vf-side', `${Math.round(half * 2)}px`);
      fitPanelToViewfinder();
    }

    photoBtn.addEventListener('click', () => {
      if (viewfinderEl.hidden) {
        openViewfinder();
        positionViewfinder();
      } else {
        closeViewfinder();
      }
    });
    viewfinderCloseBtn.addEventListener('click', closeViewfinder);
    viewfinderShootBtn.addEventListener('click', takeSpherePhoto);

    // ── Cadrage d'une photo (lien "Voir Sillages" d'Escales) ─────────────
    // /sillages?photo=sphere-<id> : même rotation, même mode globe et même
    // filtre contributeurs qu'à la prise, recul ajusté pour que le contenu
    // du carré de visée remplisse la zone utile de cet écran. Pas le
    // panneau : la sphère continue de vivre (sa forme a changé depuis).
    const sharedPhotoId = /^sphere-(\d+)$/.exec(new URLSearchParams(window.location.search).get('photo') || '')?.[1];
    const sharedViewPromise = sharedPhotoId
      ? fetchSpherePhotos()
        .then((rows) => rows.find((row) => String(row.id) === sharedPhotoId)?.view ?? null)
        .catch(() => null)
      : Promise.resolve(null);

    function applyPhotoView(view) {
      homeQuaternion.fromArray(view.home);
      orientationRad = view.orientation;
      tiltRad = view.tilt;
      updateOrientation();
      const { top, bottom } = computeSafeZone();
      const side = Math.max(50, Math.min(bottom - top, window.innerWidth - SAFE_ZONE_SIDE_MARGIN_PX * 2));
      const targetTan = Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV / 2)) * side / currentFullHeight;
      radius = THREE.MathUtils.clamp(view.radius * view.frameTan / targetTan, RADIUS_MIN, RADIUS_MAX);
      maxZoomOutRadius = Math.max(maxZoomOutRadius, radius);
      updateCamera();
      setRevealMode(view.reveal);
      if (Array.isArray(view.contributors)) setContributorFilter(new Set(view.contributors));
    }

    // ── Chargement des données ───────────────────────────────────────────
    async function fetchDeposits() {
      const [generations, arPhotos, mapPhotos] = await Promise.all([
        fetchGenerations().catch(() => []),
        fetchArPhotos().catch(() => []),
        fetchMapPhotos().catch(() => []),
      ]);
      return buildDeposits(generations, arPhotos, mapPhotos);
    }

    Promise.all([fetchDeposits(), sharedViewPromise]).then(([deposits, sharedView]) => {
      buildScene(deposits);
      const view = computeDefaultView(deposits);
      homeQuaternion.copy(view.quaternion);
      radius = view.radius;
      maxZoomOutRadius = view.radius;
      resetOrientation();
      updateCamera();
      if (sharedView) applyPhotoView(sharedView);
      depositsReady = true;
      maybeStartFade();
    });

    // ── Mise à jour en direct depuis le dashboard ─────────────────────────
    // Un dépôt ajouté/modifié/supprimé (punchline, photo AR ou photo carte)
    // ailleurs (dashboard, autre onglet) reconstruit les points et les liens,
    // sans réinitialiser le cadrage/la rotation en cours.
    let refreshTimer = null;
    function scheduleRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        fetchDeposits().then((deposits) => {
          clearScene();
          buildScene(deposits);
        });
      }, 300);
    }

    const liveEvents = createEventsStream();
    [
      'new-punchline', 'update-punchline', 'delete-punchline',
      'new-ar-photo', 'update-ar-photo', 'delete-ar-photo',
      'new-map-photo', 'update-map-photo', 'delete-map-photo',
    ].forEach((eventName) => liveEvents.addEventListener(eventName, scheduleRefresh));
    // Ajout/suppression/modif de punchline change potentiellement les poids
    // par alphabet de la sphère -- recalcul depuis les vraies données à
    // chaque fois (pas de mise à jour incrémentale : plus simple, plus sûr).
    ['new-punchline', 'update-punchline', 'delete-punchline']
      .forEach((eventName) => liveEvents.addEventListener(eventName, refreshAlphabetWeights));

    } catch (error) {
      showSillageError(error);
    }

    document.getElementById('sillage-error-retry')?.addEventListener('click', () => {
      window.location.reload();
    });
